<template>
  <div class="lbe-root">
    <!-- ── 頂部工具列 ── -->
    <div class="lbe-header">
      <button class="lbe-back-btn" @click="$emit('close')">← 返回</button>
      <div class="lbe-header-title">
        <span class="lbe-header-page">{{ pageTitle }}</span>
        <span class="lbe-header-sub">Landing Page 編輯器 · build v77 · {{ blocks.length }} 區塊</span>
      </div>
      <div class="lbe-header-tools">
        <!-- Undo / Redo -->
        <div class="lbe-undo-group">
          <button class="lbe-undo-btn" :disabled="!canUndo" @click="undo" title="復原 (Ctrl+Z)">↩</button>
          <button class="lbe-undo-btn" :disabled="!canRedo" @click="redo" title="重做 (Ctrl+Y)">↪</button>
        </div>
        <!-- Auto-save / unsaved indicator -->
        <span v-if="autoSaveStatus" class="lbe-autosave-status" :class="'as-' + autoSaveStatus">
          {{ autoSaveStatus === 'saving' ? '自動儲存中…' : autoSaveStatus === 'saved' ? '✓ 已自動儲存' : '⚠ 自動儲存失敗' }}
        </span>
        <span v-else-if="hasUnsaved" class="lbe-unsaved-dot" title="有未儲存的變更">●</span>
        <!-- Device toggle -->
        <div class="lbe-device-toggle">
          <button :class="['lbe-dev-btn', previewDevice === 'desktop' && 'active']" @click="previewDevice = 'desktop'" title="桌面">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
          </button>
          <button :class="['lbe-dev-btn', previewDevice === 'tablet' && 'active']" @click="previewDevice = 'tablet'" title="平板">
            <svg width="14" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="4" y="2" width="16" height="20" rx="2"/><line x1="10" y1="18" x2="14" y2="18"/></svg>
          </button>
          <button :class="['lbe-dev-btn', previewDevice === 'mobile' && 'active']" @click="previewDevice = 'mobile'" title="手機">
            <svg width="14" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="5" y="2" width="14" height="20" rx="2"/><circle cx="12" cy="18" r="1" fill="currentColor"/></svg>
          </button>
        </div>
        <button class="lbe-settings-btn" @click="showTheme = true" title="全站主題">🎨 主題</button>
        <button class="lbe-settings-btn" @click="loadLocalVersions(); showVersions = true" title="版本歷史">🕐 版本</button>
        <button class="lbe-settings-btn" @click="showSettings = true" title="頁面設定">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
          </svg>
          設定
        </button>
        <button class="lbe-save-btn" :disabled="saving" @click="save">
          {{ saving ? '儲存中…' : '儲存' }}
        </button>
      </div>
    </div>

    <!-- ── 頁面設定 Modal ── -->
    <div v-if="showSettings" class="lbe-settings-overlay" @click.self="showSettings = false">
      <div class="lbe-settings-panel">
        <div class="lbe-settings-header">
          <span>⚙️ 頁面設定</span>
          <button @click="showSettings = false" class="lbe-settings-close">✕</button>
        </div>
        <div class="lbe-settings-body">

          <!-- 發布狀態 -->
          <div class="lbe-sf">
            <label class="lbe-sf-label">發布狀態</label>
            <div class="lbe-status-toggle">
              <button :class="['lbe-status-btn', settings.status === 'published' && 'active-pub']"
                @click="settings.status = 'published'">
                🌐 已發布
              </button>
              <button :class="['lbe-status-btn', settings.status === 'draft' && 'active-draft']"
                @click="settings.status = 'draft'">
                📝 草稿
              </button>
            </div>
            <p class="lbe-sf-hint">草稿狀態下，公開連結不可訪問</p>
          </div>

          <!-- URL Slug -->
          <div class="lbe-sf">
            <label class="lbe-sf-label">URL Slug（公開連結路徑）</label>
            <div style="display:flex;align-items:center;gap:6px;">
              <span style="font-size:.8rem;color:#64748b;">/p/</span>
              <input v-model="settings.slug" class="lbe-sf-input" placeholder="my-page-slug" />
            </div>
            <p class="lbe-sf-hint">修改後舊連結將失效</p>
          </div>

          <div class="lbe-sf-divider"></div>

          <!-- SEO 標題 -->
          <div class="lbe-sf">
            <label class="lbe-sf-label">SEO 標題</label>
            <input v-model="settings.meta_title" class="lbe-sf-input" :placeholder="pageTitle + '（留空則使用頁面標題）'" />
            <p class="lbe-sf-hint">顯示於搜尋引擎結果，建議 50–60 字元</p>
          </div>

          <!-- SEO 描述 -->
          <div class="lbe-sf">
            <label class="lbe-sf-label">SEO 描述</label>
            <textarea v-model="settings.meta_desc" class="lbe-sf-textarea" rows="3" placeholder="頁面簡短描述，顯示於搜尋結果摘要…"></textarea>
            <p class="lbe-sf-hint">建議 120–160 字元，目前 {{ (settings.meta_desc || '').length }} 字元</p>
          </div>

          <!-- OG 圖片 -->
          <div class="lbe-sf">
            <label class="lbe-sf-label">社群分享圖片（OG Image）</label>
            <div class="lbe-img-picker lbe-img-picker-sm" @paste.prevent="e => pasteImage(e, v => settings.og_image_url = v)" @dragover.prevent @drop.prevent="e => dropImage(e, v => settings.og_image_url = v)" tabindex="0">
              <div v-if="settings.og_image_url" class="lbe-img-picker-preview">
                <img :src="settings.og_image_url" alt="OG preview" @error="e => e.target.style.opacity='.3'" />
                <button class="lbe-img-picker-clear" @click="settings.og_image_url=''" title="移除">✕</button>
              </div>
              <div v-else class="lbe-img-picker-placeholder"><span>拖放 / 貼上（Ctrl+V）圖片</span></div>
              <div class="lbe-img-picker-actions">
                <label class="lbe-img-btn">
                  📁 上傳
                  <input type="file" accept="image/*" style="display:none" @change="e => uploadImageFile(e, v => settings.og_image_url = v)" />
                </label>
                <span class="lbe-img-or">或</span>
                <input v-model="settings.og_image_url" class="lbe-input lbe-img-url-input" placeholder="貼入圖片網址…" />
              </div>
            </div>
            <p class="lbe-sf-hint">建議尺寸：1200×630px，分享到社群媒體時顯示</p>
          </div>

          <div class="lbe-sf-divider"></div>

          <!-- 感謝頁設定 -->
          <div class="lbe-sf">
            <label class="lbe-sf-label">表單送出後動作</label>
            <div class="lbe-status-toggle">
              <button :class="['lbe-status-btn', settings.thank_you_type === 'message' && 'active-pub']"
                @click="settings.thank_you_type = 'message'">💬 顯示訊息</button>
              <button :class="['lbe-status-btn', settings.thank_you_type === 'redirect' && 'active-pub']"
                @click="settings.thank_you_type = 'redirect'">↗ 跳轉頁面</button>
            </div>
          </div>
          <div v-if="settings.thank_you_type === 'message'" class="lbe-sf">
            <label class="lbe-sf-label">感謝訊息</label>
            <input v-model="settings.thank_you_content" class="lbe-sf-input" placeholder="感謝您！我們將盡快與您聯繫。" />
          </div>
          <div v-if="settings.thank_you_type === 'redirect'" class="lbe-sf">
            <label class="lbe-sf-label">跳轉網址</label>
            <input v-model="settings.thank_you_content" class="lbe-sf-input" placeholder="https://..." />
            <p class="lbe-sf-hint">送出成功後將自動跳轉至此 URL</p>
          </div>

          <div class="lbe-sf-divider"></div>

          <!-- 表單通知信箱 -->
          <div class="lbe-sf">
            <label class="lbe-sf-label">表單通知信箱 <span style="font-weight:400;color:#94a3b8;">（選填）</span></label>
            <input v-model="settings.notify_email" class="lbe-sf-input" type="email" placeholder="example@company.com" />
            <p class="lbe-sf-hint">有人送出表單後，系統將寄通知信至此信箱（含聯絡資訊與諮詢內容）</p>
          </div>

          <!-- Webhook -->
          <div class="lbe-sf">
            <label class="lbe-sf-label">Webhook URL <span style="font-weight:400;color:#94a3b8;">（選填）</span></label>
            <input v-model="settings.webhook_url" class="lbe-sf-input" placeholder="https://hooks.example.com/..." />
            <p class="lbe-sf-hint">每次表單送出後，系統將 POST JSON 至此 URL（含姓名/Email 等欄位資料）</p>
          </div>

          <div class="lbe-sf-divider"></div>

          <!-- 自動回覆信 (P4-5) -->
          <div class="lbe-sf">
            <label class="lbe-sf-label">自動回覆信 <span style="font-weight:400;color:#94a3b8;">（選填，需填表者有 email 欄位）</span></label>
            <input v-model="settings.auto_reply_subject" class="lbe-sf-input" placeholder="主旨：感謝您的來信" />
            <textarea v-model="settings.auto_reply_body" class="lbe-sf-input" rows="4" style="resize:vertical;margin-top:8px;" placeholder="內文，可用 {{name}}、{{email}} 等變數帶入填表資料"></textarea>
            <p class="lbe-sf-hint">填表者送出後會自動收到此信。內文留空則不寄送。</p>
          </div>

          <!-- CRM 自動建立 (P4-5) -->
          <div class="lbe-sf">
            <label class="lbe-status-toggle">
              <input type="checkbox" v-model="settings.crm_create" />
              <span>自動建立 CRM 聯絡人</span>
            </label>
            <p class="lbe-sf-hint">填表者 email 若不存在於 CRM，將自動建立聯絡人（來源標記為「表單」）。</p>
          </div>

          <div class="lbe-sf-divider"></div>

          <!-- 第三方追蹤碼 (P5-5) -->
          <div class="lbe-sf">
            <label class="lbe-sf-label">📊 Google Analytics 4 <span style="font-weight:400;color:#94a3b8;">（選填）</span></label>
            <input v-model="settings.ga4_id" class="lbe-sf-input" placeholder="G-XXXXXXXXXX" />
            <p class="lbe-sf-hint">填入 GA4 評估 ID，系統自動在發佈頁載入 gtag。</p>
          </div>
          <div class="lbe-sf">
            <label class="lbe-sf-label">Facebook Pixel <span style="font-weight:400;color:#94a3b8;">（選填）</span></label>
            <input v-model="settings.fb_pixel_id" class="lbe-sf-input" placeholder="像素 ID（純數字）" />
            <p class="lbe-sf-hint">填入 Pixel ID，自動載入並追蹤 PageView。</p>
          </div>
          <div class="lbe-sf">
            <label class="lbe-sf-label">自訂 &lt;head&gt; 代碼 <span style="font-weight:400;color:#94a3b8;">（進階，選填）</span></label>
            <textarea v-model="settings.head_code" class="lbe-sf-input" rows="3" style="resize:vertical;font-family:monospace;font-size:.78rem;" placeholder="<!-- GTM、其他追蹤或驗證標籤 -->"></textarea>
            <p class="lbe-sf-hint">原樣插入發佈頁 &lt;head&gt;。僅信任來源，避免貼入不明腳本。</p>
          </div>

          <div class="lbe-sf-divider"></div>

          <!-- 自訂網域 -->
          <div class="lbe-sf">
            <label class="lbe-sf-label">自訂網域 <span style="font-weight:400;color:#94a3b8;">（選填）</span></label>
            <input v-model="settings.custom_domain" class="lbe-sf-input" placeholder="landing.yourdomain.com" />
            <p class="lbe-sf-hint">設定後需在 DNS 加入 CNAME 記錄指向本伺服器，並在 Caddy 加入對應 hostname</p>
            <div v-if="settings.custom_domain" class="lbe-sf-cname-hint">
              <p style="font-size:.75rem;color:#0369a1;margin-bottom:4px;">DNS 設定範例：</p>
              <code style="font-size:.72rem;background:#e0f2fe;padding:4px 8px;border-radius:4px;display:block;">
                {{ settings.custom_domain }} CNAME aihost.chenya.biz
              </code>
            </div>
          </div>

        </div>
        <div class="lbe-settings-footer">
          <button class="lbe-btn-cancel" @click="showSettings = false">取消</button>
          <button class="lbe-btn-save" :disabled="settingsSaving" @click="saveSettings">
            {{ settingsSaving ? '儲存中…' : '儲存設定' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── 版本歷史 Modal ── -->
    <div v-if="showVersions" class="lbe-settings-overlay" @click.self="showVersions = false">
      <div class="lbe-settings-panel">
        <div class="lbe-settings-header">
          <span>🕐 版本歷史（本機儲存，最多 10 筆）</span>
          <button @click="showVersions = false" class="lbe-settings-close">✕</button>
        </div>
        <div class="lbe-settings-body">
          <div v-if="!localVersions.length" style="text-align:center;padding:2rem 0;color:#94a3b8;line-height:2;">
            尚無版本記錄<br><small>手動儲存或自動儲存後才會記錄於此</small>
          </div>
          <div v-for="(v, i) in localVersions" :key="v.ts" class="lbe-version-item">
            <div class="lbe-version-info">
              <span class="lbe-version-num">版本 {{ localVersions.length - i }}</span>
              <span class="lbe-version-time">{{ formatVersionTime(v.ts) }}</span>
              <span class="lbe-version-meta">{{ JSON.parse(v.blocks).length }} 個區塊</span>
            </div>
            <button class="lbe-btn-save" style="padding:6px 16px;font-size:.8rem;flex-shrink:0;" @click="restoreVersion(v)">恢復此版本</button>
          </div>
        </div>
        <div class="lbe-settings-footer">
          <button class="lbe-btn-cancel" @click="showVersions = false">關閉</button>
        </div>
      </div>
    </div>

    <!-- ── 全站主題 Modal (P3-1) ── -->
    <div v-if="showTheme" class="lbe-settings-overlay" @click.self="showTheme = false">
      <div class="lbe-settings-panel">
        <div class="lbe-settings-header">
          <span>🎨 全站主題</span>
          <button @click="showTheme = false" class="lbe-settings-close">✕</button>
        </div>
        <div class="lbe-settings-body">
          <!-- 一鍵配色 -->
          <div class="lbe-sf">
            <label class="lbe-sf-label">一鍵配色主題</label>
            <div class="lbe-theme-presets">
              <button v-for="(pr, key) in THEME_PRESETS" :key="key"
                :class="['lbe-theme-preset', theme.preset === key && 'active']"
                @click="applyPreset(key)" :title="pr.name">
                <span class="lbe-theme-swatch" :style="{ background: pr.primary }"></span>
                <span class="lbe-theme-swatch" :style="{ background: pr.secondary }"></span>
                <span class="lbe-theme-swatch" :style="{ background: pr.accent }"></span>
                <span class="lbe-theme-preset-name">{{ pr.name }}</span>
              </button>
            </div>
          </div>
          <div class="lbe-sf-divider"></div>
          <!-- 主題色 -->
          <div class="lbe-sf">
            <label class="lbe-sf-label">主題色系</label>
            <div class="lbe-theme-colors">
              <div class="lbe-theme-color"><span>主色 Primary</span><input type="color" v-model="theme.primary" class="lbe-color" @input="onThemeChange" /></div>
              <div class="lbe-theme-color"><span>次色 Secondary</span><input type="color" v-model="theme.secondary" class="lbe-color" @input="onThemeChange" /></div>
              <div class="lbe-theme-color"><span>強調 Accent</span><input type="color" v-model="theme.accent" class="lbe-color" @input="onThemeChange" /></div>
            </div>
            <p class="lbe-sf-hint">主色套用到按鈕、表單聚焦、FAQ、定價強調等；未單獨指定顏色的按鈕會自動採用主題色。</p>
          </div>
          <div class="lbe-sf-divider"></div>
          <!-- 字型 -->
          <div class="lbe-sf">
            <label class="lbe-sf-label">字型</label>
            <div class="lbe-field-row">
              <div class="lbe-field"><label>標題字型</label>
                <select v-model="theme.heading_font" class="lbe-input" @change="onThemeChange">
                  <option v-for="f in FONT_OPTIONS" :key="f.v" :value="f.v">{{ f.label }}</option>
                </select>
              </div>
              <div class="lbe-field"><label>內文字型</label>
                <select v-model="theme.body_font" class="lbe-input" @change="onThemeChange">
                  <option v-for="f in FONT_OPTIONS" :key="f.v" :value="f.v">{{ f.label }}</option>
                </select>
              </div>
            </div>
          </div>
          <div class="lbe-sf-divider"></div>
          <!-- 圓角 + 按鈕風格 -->
          <div class="lbe-sf">
            <label class="lbe-sf-label">圓角風格</label>
            <div class="lbe-theme-segment">
              <button v-for="o in RADIUS_OPTIONS" :key="o.v"
                :class="['lbe-seg-btn', theme.radius === o.v && 'active']"
                @click="theme.radius = o.v; onThemeChange()">{{ o.label }}</button>
            </div>
          </div>
          <div class="lbe-sf">
            <label class="lbe-sf-label">按鈕樣式</label>
            <div class="lbe-theme-segment">
              <button v-for="o in BTN_STYLE_OPTIONS" :key="o.v"
                :class="['lbe-seg-btn', theme.btn_style === o.v && 'active']"
                @click="theme.btn_style = o.v; onThemeChange()">{{ o.label }}</button>
            </div>
          </div>
        </div>
        <div class="lbe-settings-footer">
          <button class="lbe-btn-cancel" @click="theme = defaultTheme(); onThemeChange()">重設預設</button>
          <button class="lbe-btn-save" @click="showTheme = false">完成（記得按「儲存」生效）</button>
        </div>
      </div>
    </div>

    <!-- ── 進階樣式 Modal (P3-2/3-3/3-4) ── -->
    <div v-if="showAdv && selectedBlock" class="lbe-settings-overlay" @click.self="showAdv = false">
      <div class="lbe-settings-panel lbe-adv-modal">
        <div class="lbe-settings-header">
          <span>⚙ 進階樣式 — {{ typeName(selectedBlock.type) }}</span>
          <button @click="showAdv = false" class="lbe-settings-close">✕</button>
        </div>
        <div class="lbe-settings-body">
          <div class="lbe-adv-grid">
            <!-- 外觀 -->
            <div class="lbe-adv-col">
              <div class="lbe-adv-coltitle">🎨 外觀</div>
              <div class="lbe-field">
                <label>進場動畫（捲動時）</label>
                <select v-model="selectedBlock.props.adv_anim" class="lbe-input" @change="schedulePreview">
                  <option value="none">無</option><option value="fade">淡入</option>
                  <option value="up">上滑</option><option value="zoom">縮放</option>
                </select>
              </div>
              <div class="lbe-field">
                <label>背景進階</label>
                <select v-model="selectedBlock.props.adv_bg_type" class="lbe-input" @change="schedulePreview">
                  <option value="">無（沿用區塊背景色）</option>
                  <option value="gradient">漸層</option><option value="image">背景圖片</option>
                </select>
              </div>
              <template v-if="selectedBlock.props.adv_bg_type === 'gradient'">
                <div class="lbe-field-row">
                  <div class="lbe-field"><label>起始色</label><input type="color" v-model="selectedBlock.props.adv_grad_from" class="lbe-color" @input="schedulePreview" /></div>
                  <div class="lbe-field"><label>結束色</label><input type="color" v-model="selectedBlock.props.adv_grad_to" class="lbe-color" @input="schedulePreview" /></div>
                </div>
                <div class="lbe-field"><label>漸層角度 (deg)</label><input type="number" v-model.number="selectedBlock.props.adv_grad_dir" class="lbe-input" min="0" max="360" placeholder="135" @input="schedulePreview" /></div>
              </template>
              <template v-if="selectedBlock.props.adv_bg_type === 'image'">
                <div class="lbe-field"><label>背景圖片網址</label><input v-model="selectedBlock.props.adv_bg_image" class="lbe-input" placeholder="https://..." @input="schedulePreview" /></div>
                <div class="lbe-field"><label>暗化遮罩 (%)</label><input type="number" v-model.number="selectedBlock.props.adv_bg_overlay" class="lbe-input" min="0" max="100" placeholder="0" @input="schedulePreview" /></div>
              </template>
              <div class="lbe-field-row">
                <div class="lbe-field">
                  <label>陰影</label>
                  <select v-model="selectedBlock.props.adv_shadow" class="lbe-input" @change="schedulePreview">
                    <option value="">無</option><option value="sm">小</option>
                    <option value="md">中</option><option value="lg">大</option>
                  </select>
                </div>
                <div class="lbe-field">
                  <label>文字對齊</label>
                  <select v-model="selectedBlock.props.adv_text_align" class="lbe-input" @change="schedulePreview">
                    <option value="">預設</option><option value="left">靠左</option>
                    <option value="center">置中</option><option value="right">靠右</option>
                  </select>
                </div>
              </div>
            </div>
            <!-- 版面 + RWD -->
            <div class="lbe-adv-col">
              <div class="lbe-adv-coltitle">📐 版面</div>
              <div class="lbe-field-row">
                <div class="lbe-field"><label>左右留白 (px)</label><input type="number" v-model.number="selectedBlock.props.adv_pad_x" class="lbe-input" min="0" max="200" placeholder="預設" @input="schedulePreview" /></div>
                <div class="lbe-field"><label>內容最大寬 (px)</label><input type="number" v-model.number="selectedBlock.props.adv_max_w" class="lbe-input" min="320" max="1600" placeholder="預設" @input="schedulePreview" /></div>
              </div>
              <div class="lbe-field-row">
                <div class="lbe-field"><label>邊框粗細 (px)</label><input type="number" v-model.number="selectedBlock.props.adv_border_w" class="lbe-input" min="0" max="20" placeholder="0" @input="schedulePreview" /></div>
                <div class="lbe-field"><label>邊框顏色</label><input type="color" v-model="selectedBlock.props.adv_border_color" class="lbe-color" @input="schedulePreview" /></div>
              </div>
              <div class="lbe-adv-coltitle" style="margin-top:14px;">📱 響應式（RWD）</div>
              <label class="lbe-adv-check">
                <input type="checkbox" v-model="selectedBlock.props.m_hidden" @change="schedulePreview" />
                <span>手機版隱藏此區塊</span>
              </label>
              <div v-if="['features','testimonials','stats','pricing'].includes(selectedBlock.type)" class="lbe-field">
                <label>手機版欄數</label>
                <select v-model.number="selectedBlock.props.cols_mobile" class="lbe-input" @change="schedulePreview">
                  <option :value="undefined">預設</option>
                  <option :value="1">1 欄</option><option :value="2">2 欄</option>
                </select>
              </div>
              <div v-if="contrastWarn" class="lbe-contrast-warn">⚠ {{ contrastWarn }}</div>
            </div>
          </div>
        </div>
        <div class="lbe-settings-footer">
          <button class="lbe-btn-cancel" @click="resetAdv(selectedBlock.props)">清除進階樣式</button>
          <button class="lbe-btn-save" @click="showAdv = false">完成</button>
        </div>
      </div>
    </div>

    <!-- ── 主體三欄 ── -->
    <div class="lbe-body">

      <!-- 左：區塊列表 -->
      <div class="lbe-sidebar">
        <div class="lbe-sidebar-title">區塊列表</div>

        <!-- 已加入的區塊列表（可捲動） -->
        <div class="lbe-sidebar-list">
          <draggable v-model="blocks" handle=".lbe-drag-handle" @end="onDragEnd">
            <div v-for="(block, idx) in blocks" :key="block.id"
              :class="['lbe-block-item', selectedId === block.id && 'selected']" @click="select(block)">
              <span class="lbe-drag-handle" title="拖曳排序">⠿</span>
              <span class="lbe-block-icon">{{ typeIcon(block.type) }}</span>
              <span class="lbe-block-name">{{ typeName(block.type) }}</span>
              <div class="lbe-block-actions">
                <button class="lbe-act-btn" @click.stop="duplicateBlock(idx)" title="複製區塊">⎘</button>
                <button class="lbe-act-btn danger" @click.stop="removeBlock(idx)" title="刪除">✕</button>
              </div>
            </div>
          </draggable>
          <div v-if="!blocks.length" class="lbe-empty-list">尚無區塊，請新增</div>
        </div>

        <!-- 新增區塊（釘在底部，不捲動） -->
        <div class="lbe-add-section">
          <div class="lbe-add-title">新增區塊</div>
          <div class="lbe-add-grid">
            <button v-for="bt in blockTypes" :key="bt.type"
              class="lbe-add-btn" @click="addBlock(bt.type)">
              <span>{{ bt.icon }}</span>
              <span>{{ bt.name }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- 中：屬性編輯 -->
      <div class="lbe-props">
        <div v-if="!selectedBlock" class="lbe-props-empty">
          <div style="font-size:2rem;margin-bottom:.5rem;">👈</div>
          <div>點擊左側區塊進行編輯</div>
        </div>

        <template v-else>
          <div class="lbe-props-title">{{ typeName(selectedBlock.type) }} 設定</div>

          <!-- Hero -->
          <template v-if="selectedBlock.type === 'hero'">
            <div class="lbe-field"><label>主標題文字</label><input v-model="selectedBlock.props.title" class="lbe-input" @input="schedulePreview" /></div>
            <div class="lbe-field"><label>副標題文字</label><input v-model="selectedBlock.props.subtitle" class="lbe-input" @input="schedulePreview" /></div>
            <div class="lbe-field-row">
              <div class="lbe-field"><label>區塊背景顏色</label><input type="color" v-model="selectedBlock.props.bg_color" class="lbe-color" @input="schedulePreview" /></div>
              <div class="lbe-field"><label>文字顏色</label><input type="color" v-model="selectedBlock.props.text_color" class="lbe-color" @input="schedulePreview" /></div>
            </div>
            <div class="lbe-field"><label>按鈕顯示文字</label><input v-model="selectedBlock.props.cta_text" class="lbe-input" placeholder="例：立即了解、免費試用" @input="schedulePreview" /></div>
            <div class="lbe-field"><label>按鈕點擊後前往的網址</label><input v-model="selectedBlock.props.cta_url" class="lbe-input" placeholder="https://example.com 或 #lp-form（頁內錨點）" @input="schedulePreview" /></div>
            <div class="lbe-field-row">
              <div class="lbe-field"><label>按鈕背景顏色</label><input type="color" v-model="selectedBlock.props.cta_color" class="lbe-color" @input="schedulePreview" /></div>
              <div class="lbe-field"><label>按鈕文字顏色</label><input type="color" v-model="selectedBlock.props.cta_text_color" class="lbe-color" @input="schedulePreview" /></div>
            </div>
            <div class="lbe-field"><label>上下留白 (px)</label><input type="number" v-model.number="selectedBlock.props.padding_y" class="lbe-input" min="0" max="200" @input="schedulePreview" /></div>
          </template>

          <!-- Text -->
          <template v-else-if="selectedBlock.type === 'text'">
            <div class="lbe-field">
              <label>內容（支援 HTML 標籤）</label>
              <textarea v-model="selectedBlock.props.html" class="lbe-textarea lbe-textarea-lg" @input="schedulePreview" placeholder="<p>輸入內容…</p>&#10;<h2>標題</h2>&#10;<ul><li>項目</li></ul>"></textarea>
            </div>
            <div class="lbe-field">
              <label>文字對齊</label>
              <select v-model="selectedBlock.props.align" class="lbe-select" @change="schedulePreview">
                <option value="left">靠左</option>
                <option value="center">置中</option>
                <option value="right">靠右</option>
              </select>
            </div>
            <div class="lbe-field-row">
              <div class="lbe-field"><label>區塊背景顏色</label><input type="color" v-model="selectedBlock.props.bg_color" class="lbe-color" @input="schedulePreview" /></div>
              <div class="lbe-field"><label>文字顏色</label><input type="color" v-model="selectedBlock.props.text_color" class="lbe-color" @input="schedulePreview" /></div>
            </div>
            <div class="lbe-field"><label>上下留白 (px)</label><input type="number" v-model.number="selectedBlock.props.padding_y" class="lbe-input" min="0" max="200" @input="schedulePreview" /></div>
          </template>

          <!-- Features -->
          <template v-else-if="selectedBlock.type === 'features'">
            <div class="lbe-field"><label>區塊標題</label><input v-model="selectedBlock.props.title" class="lbe-input" @input="schedulePreview" /></div>
            <div class="lbe-field">
              <label>每列欄數</label>
              <select v-model.number="selectedBlock.props.columns" class="lbe-select" @change="schedulePreview">
                <option :value="2">2 欄</option>
                <option :value="3">3 欄</option>
                <option :value="4">4 欄</option>
              </select>
            </div>
            <div class="lbe-field"><label>區塊背景顏色</label><input type="color" v-model="selectedBlock.props.bg_color" class="lbe-color" @input="schedulePreview" /></div>
            <div class="lbe-field"><label>上下留白 (px)</label><input type="number" v-model.number="selectedBlock.props.padding_y" class="lbe-input" min="0" max="200" @input="schedulePreview" /></div>
            <!-- Items -->
            <div class="lbe-sub-title">特色項目</div>
            <div v-for="(item, i) in selectedBlock.props.items" :key="i" class="lbe-sub-item">
              <div class="lbe-sub-item-header">
                <span>項目 {{ i + 1 }}</span>
                <button class="lbe-act-btn danger" @click="removeFeatureItem(i)">✕</button>
              </div>
              <div class="lbe-field"><label>圖示（Emoji，如 🚀）</label><input v-model="item.icon" class="lbe-input" @input="schedulePreview" /></div>
              <div class="lbe-field"><label>項目標題</label><input v-model="item.title" class="lbe-input" @input="schedulePreview" /></div>
              <div class="lbe-field"><label>項目說明</label><input v-model="item.desc" class="lbe-input" @input="schedulePreview" /></div>
            </div>
            <button class="lbe-add-item-btn" @click="addFeatureItem">+ 新增項目</button>
          </template>

          <!-- Image -->
          <template v-else-if="selectedBlock.type === 'image'">
            <div class="lbe-field">
              <label>圖片</label>
              <div class="lbe-img-picker" @paste.prevent="e => pasteImage(e, v => { selectedBlock.props.url = v; schedulePreview() })" @dragover.prevent @drop.prevent="e => dropImage(e, v => { selectedBlock.props.url = v; schedulePreview() })" tabindex="0">
                <div v-if="selectedBlock.props.url" class="lbe-img-picker-preview">
                  <img :src="selectedBlock.props.url" alt="preview" @error="e => e.target.style.opacity='.3'" />
                  <button class="lbe-img-picker-clear" @click="selectedBlock.props.url=''; schedulePreview()" title="移除">✕</button>
                </div>
                <div v-else class="lbe-img-picker-placeholder">
                  <span>拖放 / 貼上（Ctrl+V）圖片至此</span>
                </div>
                <div class="lbe-img-picker-actions">
                  <label class="lbe-img-btn">
                    📁 上傳圖片
                    <input type="file" accept="image/*" style="display:none" @change="e => uploadImageFile(e, v => { selectedBlock.props.url = v; schedulePreview() })" />
                  </label>
                  <span class="lbe-img-or">或</span>
                  <input v-model="selectedBlock.props.url" class="lbe-input lbe-img-url-input" placeholder="貼入圖片網址…" @input="schedulePreview" />
                </div>
                <p v-if="imgUploading" class="lbe-img-hint">上傳中…</p>
                <p v-if="imgError" class="lbe-img-error">{{ imgError }}</p>
              </div>
            </div>
            <div class="lbe-field"><label>圖片說明文字（SEO 用，不顯示於畫面）</label><input v-model="selectedBlock.props.alt" class="lbe-input" @input="schedulePreview" /></div>
            <div class="lbe-field"><label>圖片最大寬度 (px)</label><input type="number" v-model.number="selectedBlock.props.max_width" class="lbe-input" min="100" max="1200" @input="schedulePreview" /></div>
            <div class="lbe-field">
              <label>圖片對齊</label>
              <select v-model="selectedBlock.props.align" class="lbe-select" @change="schedulePreview">
                <option value="center">置中</option>
                <option value="left">靠左</option>
                <option value="right">靠右</option>
              </select>
            </div>
            <div class="lbe-field-row">
              <div class="lbe-field"><label>區塊背景顏色</label><input type="color" v-model="selectedBlock.props.bg_color" class="lbe-color" @input="schedulePreview" /></div>
              <div class="lbe-field"><label>上下留白 (px)</label><input type="number" v-model.number="selectedBlock.props.padding_y" class="lbe-input" min="0" max="200" @input="schedulePreview" /></div>
            </div>
          </template>

          <!-- CTA -->
          <template v-else-if="selectedBlock.type === 'cta'">
            <div class="lbe-field"><label>標題文字</label><input v-model="selectedBlock.props.title" class="lbe-input" @input="schedulePreview" /></div>
            <div class="lbe-field"><label>副標題文字</label><input v-model="selectedBlock.props.subtitle" class="lbe-input" @input="schedulePreview" /></div>
            <div class="lbe-field"><label>按鈕顯示文字</label><input v-model="selectedBlock.props.btn_text" class="lbe-input" placeholder="例：立即購買、了解更多" @input="schedulePreview" /></div>
            <div class="lbe-field"><label>按鈕點擊後前往的網址</label><input v-model="selectedBlock.props.btn_url" class="lbe-input" placeholder="https://example.com 或 #lp-form（頁內錨點）" @input="schedulePreview" /></div>
            <div class="lbe-field-row">
              <div class="lbe-field"><label>區塊背景顏色</label><input type="color" v-model="selectedBlock.props.bg_color" class="lbe-color" @input="schedulePreview" /></div>
              <div class="lbe-field"><label>文字顏色</label><input type="color" v-model="selectedBlock.props.text_color" class="lbe-color" @input="schedulePreview" /></div>
            </div>
            <div class="lbe-field-row">
              <div class="lbe-field"><label>按鈕背景顏色</label><input type="color" v-model="selectedBlock.props.btn_color" class="lbe-color" @input="schedulePreview" /></div>
              <div class="lbe-field"><label>按鈕文字顏色</label><input type="color" v-model="selectedBlock.props.btn_text_color" class="lbe-color" @input="schedulePreview" /></div>
            </div>
            <div class="lbe-field"><label>上下留白 (px)</label><input type="number" v-model.number="selectedBlock.props.padding_y" class="lbe-input" min="0" max="200" @input="schedulePreview" /></div>
          </template>

          <!-- Form -->
          <template v-else-if="selectedBlock.type === 'form'">
            <div class="lbe-field"><label>表單標題</label><input v-model="selectedBlock.props.title" class="lbe-input" @input="schedulePreview" /></div>
            <div class="lbe-field"><label>送出按鈕文字</label><input v-model="selectedBlock.props.submit_text" class="lbe-input" @input="schedulePreview" /></div>
            <div class="lbe-field"><label>送出按鈕顏色</label><input type="color" v-model="selectedBlock.props.btn_color" class="lbe-color" @input="schedulePreview" /></div>
            <div class="lbe-field"><label>送出成功後顯示的訊息</label><input v-model="selectedBlock.props.success_message" class="lbe-input" placeholder="感謝您！我們將盡快與您聯繫。" @input="schedulePreview" /></div>
            <div class="lbe-field"><label>區塊背景顏色</label><input type="color" v-model="selectedBlock.props.bg_color" class="lbe-color" @input="schedulePreview" /></div>
            <div class="lbe-field"><label>上下留白 (px)</label><input type="number" v-model.number="selectedBlock.props.padding_y" class="lbe-input" min="0" max="200" @input="schedulePreview" /></div>
            <!-- Fields -->
            <div class="lbe-sub-title">表單欄位</div>
            <div v-for="(field, fi) in selectedBlock.props.fields" :key="fi" class="lbe-sub-item">
              <div class="lbe-sub-item-header">
                <span>欄位 {{ fi + 1 }}</span>
                <button class="lbe-act-btn danger" @click="removeFormField(fi)">✕</button>
              </div>
              <div class="lbe-field"><label>欄位識別碼（英文，如 email、phone）</label><input v-model="field.name" class="lbe-input" @input="schedulePreview" /></div>
              <div class="lbe-field"><label>欄位顯示名稱（用戶看到的標題）</label><input v-model="field.label" class="lbe-input" @input="schedulePreview" /></div>
              <div class="lbe-field-row">
                <div class="lbe-field">
                  <label>輸入類型</label>
                  <select v-model="field.type" class="lbe-select" @change="schedulePreview">
                    <option value="text">單行文字</option>
                    <option value="email">Email 信箱</option>
                    <option value="tel">電話號碼</option>
                    <option value="number">數字</option>
                    <option value="date">日期</option>
                    <option value="textarea">多行文字</option>
                    <option value="select">下拉選單</option>
                    <option value="radio">單選</option>
                    <option value="checkbox-group">多選</option>
                    <option value="rating">星等評分</option>
                    <option value="consent">同意條款</option>
                    <option value="hidden">隱藏欄位</option>
                  </select>
                </div>
                <div class="lbe-field">
                  <label>欄寬</label>
                  <select v-model="field.width" class="lbe-select" @change="schedulePreview">
                    <option value="full">整行</option><option value="half">半行</option>
                  </select>
                </div>
              </div>
              <div v-if="!['hidden','consent','rating'].includes(field.type)" class="lbe-field"><label>輸入框提示文字（灰色佔位符）</label><input v-model="field.placeholder" class="lbe-input" @input="schedulePreview" /></div>
              <div v-if="field.type === 'hidden'" class="lbe-field"><label>固定值（如 UTM 來源）</label><input v-model="field.default" class="lbe-input" @input="schedulePreview" /></div>
              <div v-if="field.type === 'consent'" class="lbe-field"><label>條款連結網址（選填）</label><input v-model="field.consent_link" class="lbe-input" placeholder="/privacy" @input="schedulePreview" /></div>
              <div v-if="field.type !== 'hidden'" class="lbe-field lbe-field-check">
                <input type="checkbox" v-model="field.required" @change="schedulePreview" />
                <label>此欄位必填</label>
              </div>
              <div v-if="['select','radio','checkbox-group'].includes(field.type)" class="lbe-field">
                <label>選項（每行一個）</label>
                <textarea class="lbe-textarea"
                  :value="(field.options || []).join('\n')"
                  @input="e => { field.options = e.target.value.split('\n').map(s => s.trim()).filter(Boolean); schedulePreview() }"
                ></textarea>
              </div>
              <div v-if="!['hidden','consent'].includes(field.type)" class="lbe-field"><label>欄位說明文字（選填）</label><input v-model="field.help" class="lbe-input" placeholder="顯示在欄位下方的灰色提示" @input="schedulePreview" /></div>
              <!-- 驗證 -->
              <div v-if="['text','email','tel','textarea'].includes(field.type)" class="lbe-field-row">
                <div class="lbe-field"><label>最短字數</label><input type="number" v-model.number="field.minlength" class="lbe-input" min="0" @input="schedulePreview" /></div>
                <div class="lbe-field"><label>最長字數</label><input type="number" v-model.number="field.maxlength" class="lbe-input" min="0" @input="schedulePreview" /></div>
              </div>
              <div v-if="field.type === 'number'" class="lbe-field-row">
                <div class="lbe-field"><label>最小值</label><input type="number" v-model.number="field.min" class="lbe-input" @input="schedulePreview" /></div>
                <div class="lbe-field"><label>最大值</label><input type="number" v-model.number="field.max" class="lbe-input" @input="schedulePreview" /></div>
              </div>
              <!-- 進階：條件顯示 + 多步驟 -->
              <details class="lbe-field-adv">
                <summary>進階：條件顯示 / 多步驟</summary>
                <div class="lbe-field"><label>當此欄位（識別碼）</label><input v-model="field.show_if_field" class="lbe-input" placeholder="留空 = 永遠顯示" @input="schedulePreview" /></div>
                <div class="lbe-field"><label>等於此值時才顯示</label><input v-model="field.show_if_value" class="lbe-input" @input="schedulePreview" /></div>
                <div class="lbe-field"><label>所屬步驟（0,1,2…；留空=不分步）</label><input type="number" v-model.number="field.step" class="lbe-input" min="0" @input="schedulePreview" /></div>
              </details>
            </div>
            <button class="lbe-add-item-btn" @click="addFormField">+ 新增欄位</button>
          </template>

          <!-- Divider -->
          <template v-else-if="selectedBlock.type === 'divider'">
            <div class="lbe-field"><label>線條顏色</label><input type="color" v-model="selectedBlock.props.color" class="lbe-color" @input="schedulePreview" /></div>
            <div class="lbe-field"><label>線條粗細 (px)</label><input type="number" v-model.number="selectedBlock.props.height" class="lbe-input" min="1" max="20" @input="schedulePreview" /></div>
            <div class="lbe-field">
              <label>線條樣式</label>
              <select v-model="selectedBlock.props.style" class="lbe-select" @change="schedulePreview">
                <option value="solid">實線</option>
                <option value="dashed">虛線</option>
                <option value="dotted">點線</option>
              </select>
            </div>
            <div class="lbe-field"><label>上下外距 (px)</label><input type="number" v-model.number="selectedBlock.props.margin_y" class="lbe-input" min="0" max="100" @input="schedulePreview" /></div>
          </template>

          <!-- HTML -->
          <template v-else-if="selectedBlock.type === 'html'">
            <div class="lbe-field">
              <label>自訂 HTML 程式碼</label>
              <textarea v-model="selectedBlock.props.code" class="lbe-textarea lbe-textarea-xl" @input="schedulePreview" placeholder="<div>...</div>"></textarea>
            </div>
            <div class="lbe-field"><label>上下留白 (px)</label><input type="number" v-model.number="selectedBlock.props.padding_y" class="lbe-input" min="0" max="200" @input="schedulePreview" /></div>
          </template>

          <!-- Testimonials -->
          <template v-else-if="selectedBlock.type === 'testimonials'">
            <div class="lbe-field"><label>區塊標題</label><input v-model="selectedBlock.props.title" class="lbe-input" @input="schedulePreview" /></div>
            <div class="lbe-field">
              <label>每列欄數</label>
              <select v-model.number="selectedBlock.props.columns" class="lbe-select" @change="schedulePreview">
                <option :value="1">1 欄</option><option :value="2">2 欄</option><option :value="3">3 欄</option>
              </select>
            </div>
            <div class="lbe-field-row">
              <div class="lbe-field"><label>背景顏色</label><input type="color" v-model="selectedBlock.props.bg_color" class="lbe-color" @input="schedulePreview" /></div>
              <div class="lbe-field"><label>文字顏色</label><input type="color" v-model="selectedBlock.props.text_color" class="lbe-color" @input="schedulePreview" /></div>
            </div>
            <div class="lbe-field"><label>上下留白 (px)</label><input type="number" v-model.number="selectedBlock.props.padding_y" class="lbe-input" min="0" max="200" @input="schedulePreview" /></div>
            <div class="lbe-sub-title">見證項目</div>
            <div v-for="(item, i) in selectedBlock.props.items" :key="i" class="lbe-sub-item">
              <div class="lbe-sub-item-header"><span>見證 {{ i + 1 }}</span><button class="lbe-act-btn danger" @click="selectedBlock.props.items.splice(i,1);schedulePreview()">✕</button></div>
              <div class="lbe-field"><label>姓名</label><input v-model="item.name" class="lbe-input" @input="schedulePreview" /></div>
              <div class="lbe-field"><label>職稱 / 公司</label><input v-model="item.role" class="lbe-input" @input="schedulePreview" /></div>
              <div class="lbe-field"><label>評語內容</label><textarea v-model="item.quote" class="lbe-textarea" @input="schedulePreview"></textarea></div>
              <div class="lbe-field"><label>頭像圖片網址（選填）</label><input v-model="item.avatar" class="lbe-input" placeholder="https://..." @input="schedulePreview" /></div>
              <div class="lbe-field"><label>評分（1–5 顆星）</label><input type="number" v-model.number="item.rating" class="lbe-input" min="1" max="5" @input="schedulePreview" /></div>
            </div>
            <button class="lbe-add-item-btn" @click="selectedBlock.props.items.push({name:'',role:'',quote:'',avatar:'',rating:5});schedulePreview()">+ 新增見證</button>
          </template>

          <!-- Video -->
          <template v-else-if="selectedBlock.type === 'video'">
            <div class="lbe-field"><label>影片網址</label><input v-model="selectedBlock.props.url" class="lbe-input" placeholder="YouTube / Vimeo 連結，或直接 .mp4 網址" @input="schedulePreview" /><p class="lbe-sf-hint" style="margin-top:4px;">支援 YouTube、Vimeo 網址或直接 MP4 連結</p></div>
            <div class="lbe-field">
              <label>畫面比例</label>
              <select v-model="selectedBlock.props.aspect" class="lbe-select" @change="schedulePreview">
                <option value="16:9">16:9（橫向標準）</option>
                <option value="4:3">4:3（傳統）</option>
                <option value="1:1">1:1（正方形）</option>
              </select>
            </div>
            <div class="lbe-field"><label>最大寬度 (px)</label><input type="number" v-model.number="selectedBlock.props.max_width" class="lbe-input" min="300" max="1200" @input="schedulePreview" /></div>
            <div class="lbe-field"><label>圖說文字（選填）</label><input v-model="selectedBlock.props.caption" class="lbe-input" @input="schedulePreview" /></div>
            <div class="lbe-field"><label>背景顏色</label><input type="color" v-model="selectedBlock.props.bg_color" class="lbe-color" @input="schedulePreview" /></div>
            <div class="lbe-field"><label>上下留白 (px)</label><input type="number" v-model.number="selectedBlock.props.padding_y" class="lbe-input" min="0" max="200" @input="schedulePreview" /></div>
          </template>

          <!-- Stats -->
          <template v-else-if="selectedBlock.type === 'stats'">
            <div class="lbe-field"><label>區塊標題（選填）</label><input v-model="selectedBlock.props.title" class="lbe-input" @input="schedulePreview" /></div>
            <div class="lbe-field">
              <label>每列欄數</label>
              <select v-model.number="selectedBlock.props.columns" class="lbe-select" @change="schedulePreview">
                <option :value="2">2 欄</option><option :value="3">3 欄</option><option :value="4">4 欄</option>
              </select>
            </div>
            <div class="lbe-field-row">
              <div class="lbe-field"><label>背景顏色</label><input type="color" v-model="selectedBlock.props.bg_color" class="lbe-color" @input="schedulePreview" /></div>
              <div class="lbe-field"><label>文字顏色</label><input type="color" v-model="selectedBlock.props.text_color" class="lbe-color" @input="schedulePreview" /></div>
            </div>
            <div class="lbe-field"><label>數字強調色</label><input type="color" v-model="selectedBlock.props.accent_color" class="lbe-color" @input="schedulePreview" /></div>
            <div class="lbe-field"><label>上下留白 (px)</label><input type="number" v-model.number="selectedBlock.props.padding_y" class="lbe-input" min="0" max="200" @input="schedulePreview" /></div>
            <div class="lbe-sub-title">統計項目</div>
            <div v-for="(item, i) in selectedBlock.props.items" :key="i" class="lbe-sub-item">
              <div class="lbe-sub-item-header"><span>項目 {{ i + 1 }}</span><button class="lbe-act-btn danger" @click="selectedBlock.props.items.splice(i,1);schedulePreview()">✕</button></div>
              <div class="lbe-field-row">
                <div class="lbe-field"><label>數字</label><input v-model="item.number" class="lbe-input" placeholder="10,000" @input="schedulePreview" /></div>
                <div class="lbe-field"><label>單位</label><input v-model="item.unit" class="lbe-input" placeholder="+" @input="schedulePreview" /></div>
              </div>
              <div class="lbe-field"><label>說明文字</label><input v-model="item.label" class="lbe-input" @input="schedulePreview" /></div>
            </div>
            <button class="lbe-add-item-btn" @click="selectedBlock.props.items.push({number:'',unit:'',label:''});schedulePreview()">+ 新增統計</button>
          </template>

          <!-- Pricing -->
          <template v-else-if="selectedBlock.type === 'pricing'">
            <div class="lbe-field"><label>區塊標題</label><input v-model="selectedBlock.props.title" class="lbe-input" @input="schedulePreview" /></div>
            <div class="lbe-field"><label>副標題說明</label><input v-model="selectedBlock.props.subtitle" class="lbe-input" @input="schedulePreview" /></div>
            <div class="lbe-field-row">
              <div class="lbe-field"><label>背景顏色</label><input type="color" v-model="selectedBlock.props.bg_color" class="lbe-color" @input="schedulePreview" /></div>
              <div class="lbe-field"><label>上下留白 (px)</label><input type="number" v-model.number="selectedBlock.props.padding_y" class="lbe-input" min="0" max="200" @input="schedulePreview" /></div>
            </div>
            <div class="lbe-sub-title">方案（最多 3 個）</div>
            <div v-for="(item, i) in selectedBlock.props.items" :key="i" class="lbe-sub-item">
              <div class="lbe-sub-item-header"><span>方案 {{ i + 1 }}</span><button class="lbe-act-btn danger" @click="selectedBlock.props.items.splice(i,1);schedulePreview()">✕</button></div>
              <div class="lbe-field"><label>方案名稱</label><input v-model="item.name" class="lbe-input" @input="schedulePreview" /></div>
              <div class="lbe-field-row">
                <div class="lbe-field"><label>價格文字</label><input v-model="item.price" class="lbe-input" placeholder="NT$299" @input="schedulePreview" /></div>
                <div class="lbe-field"><label>計價單位</label><input v-model="item.unit" class="lbe-input" placeholder="/ 月" @input="schedulePreview" /></div>
              </div>
              <div class="lbe-field"><label>方案簡介</label><input v-model="item.desc" class="lbe-input" @input="schedulePreview" /></div>
              <div class="lbe-field">
                <label>功能列表（每行一項）</label>
                <textarea class="lbe-textarea"
                  :value="(item.features||[]).join('\n')"
                  @input="e=>{item.features=e.target.value.split('\n').map(s=>s.trim()).filter(Boolean);schedulePreview()}"
                ></textarea>
              </div>
              <div class="lbe-field-row">
                <div class="lbe-field"><label>按鈕文字</label><input v-model="item.cta_text" class="lbe-input" @input="schedulePreview" /></div>
                <div class="lbe-field"><label>按鈕顏色</label><input type="color" v-model="item.cta_color" class="lbe-color" @input="schedulePreview" /></div>
              </div>
              <div class="lbe-field"><label>按鈕連結</label><input v-model="item.cta_url" class="lbe-input" placeholder="#lp-form" @input="schedulePreview" /></div>
              <div class="lbe-field lbe-field-check">
                <input type="checkbox" v-model="item.highlighted" @change="schedulePreview" />
                <label>推薦方案（顯示邊框與徽章）</label>
              </div>
              <div v-if="item.highlighted" class="lbe-field"><label>徽章文字</label><input v-model="item.badge" class="lbe-input" placeholder="推薦方案" @input="schedulePreview" /></div>
            </div>
            <button v-if="(selectedBlock.props.items||[]).length < 3" class="lbe-add-item-btn" @click="selectedBlock.props.items.push({name:'新方案',price:'NT$0',unit:'/ 月',desc:'',highlighted:false,badge:'',cta_text:'立即選擇',cta_url:'#lp-form',cta_color:'#4f46e5',features:[]});schedulePreview()">+ 新增方案</button>
          </template>

          <!-- FAQ -->
          <template v-else-if="selectedBlock.type === 'faq'">
            <div class="lbe-field"><label>區塊標題</label><input v-model="selectedBlock.props.title" class="lbe-input" @input="schedulePreview" /></div>
            <div class="lbe-field-row">
              <div class="lbe-field"><label>背景顏色</label><input type="color" v-model="selectedBlock.props.bg_color" class="lbe-color" @input="schedulePreview" /></div>
              <div class="lbe-field"><label>上下留白 (px)</label><input type="number" v-model.number="selectedBlock.props.padding_y" class="lbe-input" min="0" max="200" @input="schedulePreview" /></div>
            </div>
            <div class="lbe-sub-title">問答項目</div>
            <div v-for="(item, i) in selectedBlock.props.items" :key="i" class="lbe-sub-item">
              <div class="lbe-sub-item-header"><span>問答 {{ i + 1 }}</span><button class="lbe-act-btn danger" @click="selectedBlock.props.items.splice(i,1);schedulePreview()">✕</button></div>
              <div class="lbe-field"><label>問題</label><input v-model="item.question" class="lbe-input" @input="schedulePreview" /></div>
              <div class="lbe-field"><label>回答</label><textarea v-model="item.answer" class="lbe-textarea" @input="schedulePreview"></textarea></div>
            </div>
            <button class="lbe-add-item-btn" @click="selectedBlock.props.items.push({question:'',answer:''});schedulePreview()">+ 新增問答</button>
          </template>

          <!-- Logos -->
          <template v-else-if="selectedBlock.type === 'logos'">
            <div class="lbe-field"><label>標題文字（選填）</label><input v-model="selectedBlock.props.title" class="lbe-input" placeholder="合作夥伴" @input="schedulePreview" /></div>
            <div class="lbe-field-row">
              <div class="lbe-field"><label>背景顏色</label><input type="color" v-model="selectedBlock.props.bg_color" class="lbe-color" @input="schedulePreview" /></div>
              <div class="lbe-field"><label>上下留白 (px)</label><input type="number" v-model.number="selectedBlock.props.padding_y" class="lbe-input" min="0" max="200" @input="schedulePreview" /></div>
            </div>
            <div class="lbe-field lbe-field-check">
              <input type="checkbox" v-model="selectedBlock.props.grayscale" @change="schedulePreview" />
              <label>Logo 灰階顯示（hover 時恢復彩色）</label>
            </div>
            <div class="lbe-sub-title">Logo 項目</div>
            <div v-for="(item, i) in selectedBlock.props.items" :key="i" class="lbe-sub-item">
              <div class="lbe-sub-item-header"><span>Logo {{ i + 1 }}</span><button class="lbe-act-btn danger" @click="selectedBlock.props.items.splice(i,1);schedulePreview()">✕</button></div>
              <div class="lbe-field">
                <label>Logo 圖片</label>
                <div class="lbe-img-picker lbe-img-picker-sm" @paste.prevent="e=>pasteImage(e,v=>{item.url=v;schedulePreview()})" @dragover.prevent @drop.prevent="e=>dropImage(e,v=>{item.url=v;schedulePreview()})" tabindex="0">
                  <div v-if="item.url" class="lbe-img-picker-preview"><img :src="item.url" alt="logo" @error="e=>e.target.style.opacity='.3'" /><button class="lbe-img-picker-clear" @click="item.url='';schedulePreview()" title="移除">✕</button></div>
                  <div v-else class="lbe-img-picker-placeholder"><span>拖放 / 貼上圖片</span></div>
                  <div class="lbe-img-picker-actions">
                    <label class="lbe-img-btn">📁 上傳<input type="file" accept="image/*" style="display:none" @change="e=>uploadImageFile(e,v=>{item.url=v;schedulePreview()})" /></label>
                    <span class="lbe-img-or">或</span>
                    <input v-model="item.url" class="lbe-input lbe-img-url-input" placeholder="圖片網址…" @input="schedulePreview" />
                  </div>
                </div>
              </div>
              <div class="lbe-field"><label>Alt 文字</label><input v-model="item.alt" class="lbe-input" @input="schedulePreview" /></div>
              <div class="lbe-field"><label>點擊連結（選填）</label><input v-model="item.link" class="lbe-input" placeholder="https://..." @input="schedulePreview" /></div>
            </div>
            <button class="lbe-add-item-btn" @click="selectedBlock.props.items.push({url:'',alt:'',link:''});schedulePreview()">+ 新增 Logo</button>
          </template>

          <!-- Image Text -->
          <template v-else-if="selectedBlock.type === 'image_text'">
            <div class="lbe-field">
              <label>圖片</label>
              <div class="lbe-img-picker" @paste.prevent="e=>pasteImage(e,v=>{selectedBlock.props.image_url=v;schedulePreview()})" @dragover.prevent @drop.prevent="e=>dropImage(e,v=>{selectedBlock.props.image_url=v;schedulePreview()})" tabindex="0">
                <div v-if="selectedBlock.props.image_url" class="lbe-img-picker-preview"><img :src="selectedBlock.props.image_url" alt="preview" @error="e=>e.target.style.opacity='.3'" /><button class="lbe-img-picker-clear" @click="selectedBlock.props.image_url='';schedulePreview()" title="移除">✕</button></div>
                <div v-else class="lbe-img-picker-placeholder"><span>拖放 / 貼上（Ctrl+V）圖片至此</span></div>
                <div class="lbe-img-picker-actions">
                  <label class="lbe-img-btn">📁 上傳圖片<input type="file" accept="image/*" style="display:none" @change="e=>uploadImageFile(e,v=>{selectedBlock.props.image_url=v;schedulePreview()})" /></label>
                  <span class="lbe-img-or">或</span>
                  <input v-model="selectedBlock.props.image_url" class="lbe-input lbe-img-url-input" placeholder="圖片網址…" @input="schedulePreview" />
                </div>
              </div>
            </div>
            <div class="lbe-field"><label>圖片 Alt 文字（SEO）</label><input v-model="selectedBlock.props.image_alt" class="lbe-input" @input="schedulePreview" /></div>
            <div class="lbe-field">
              <label>版型</label>
              <select v-model="selectedBlock.props.layout" class="lbe-select" @change="schedulePreview">
                <option value="image_left">圖片在左、文字在右</option>
                <option value="image_right">文字在左、圖片在右</option>
              </select>
            </div>
            <div class="lbe-field"><label>標題</label><input v-model="selectedBlock.props.title" class="lbe-input" @input="schedulePreview" /></div>
            <div class="lbe-field"><label>內容（支援 HTML）</label><textarea v-model="selectedBlock.props.html" class="lbe-textarea lbe-textarea-lg" @input="schedulePreview"></textarea></div>
            <div class="lbe-field"><label>按鈕文字（選填）</label><input v-model="selectedBlock.props.cta_text" class="lbe-input" @input="schedulePreview" /></div>
            <div v-if="selectedBlock.props.cta_text">
              <div class="lbe-field"><label>按鈕連結</label><input v-model="selectedBlock.props.cta_url" class="lbe-input" @input="schedulePreview" /></div>
              <div class="lbe-field-row">
                <div class="lbe-field"><label>按鈕背景色</label><input type="color" v-model="selectedBlock.props.cta_color" class="lbe-color" @input="schedulePreview" /></div>
                <div class="lbe-field"><label>按鈕文字色</label><input type="color" v-model="selectedBlock.props.cta_text_color" class="lbe-color" @input="schedulePreview" /></div>
              </div>
            </div>
            <div class="lbe-field-row">
              <div class="lbe-field"><label>背景顏色</label><input type="color" v-model="selectedBlock.props.bg_color" class="lbe-color" @input="schedulePreview" /></div>
              <div class="lbe-field"><label>文字顏色</label><input type="color" v-model="selectedBlock.props.text_color" class="lbe-color" @input="schedulePreview" /></div>
            </div>
            <div class="lbe-field"><label>上下留白 (px)</label><input type="number" v-model.number="selectedBlock.props.padding_y" class="lbe-input" min="0" max="200" @input="schedulePreview" /></div>
          </template>

          <!-- Countdown -->
          <template v-else-if="selectedBlock.type === 'countdown'">
            <div class="lbe-field"><label>標題</label><input v-model="selectedBlock.props.title" class="lbe-input" @input="schedulePreview" /></div>
            <div class="lbe-field"><label>副標題</label><input v-model="selectedBlock.props.subtitle" class="lbe-input" @input="schedulePreview" /></div>
            <div class="lbe-field">
              <label>截止日期 / 時間</label>
              <input type="datetime-local" v-model="selectedBlock.props.target_date" class="lbe-input" @input="schedulePreview" />
            </div>
            <div class="lbe-field"><label>到期後顯示的文字</label><input v-model="selectedBlock.props.expired_text" class="lbe-input" placeholder="活動已結束" @input="schedulePreview" /></div>
            <div class="lbe-field-row">
              <div class="lbe-field"><label>背景顏色</label><input type="color" v-model="selectedBlock.props.bg_color" class="lbe-color" @input="schedulePreview" /></div>
              <div class="lbe-field"><label>文字顏色</label><input type="color" v-model="selectedBlock.props.text_color" class="lbe-color" @input="schedulePreview" /></div>
            </div>
            <div class="lbe-field"><label>數字強調色</label><input type="color" v-model="selectedBlock.props.accent_color" class="lbe-color" @input="schedulePreview" /></div>
            <div class="lbe-field"><label>上下留白 (px)</label><input type="number" v-model.number="selectedBlock.props.padding_y" class="lbe-input" min="0" max="200" @input="schedulePreview" /></div>
          </template>

          <!-- 進階樣式 (P3-2) — 適用所有區塊；改以彈出視窗呈現 -->
          <template v-if="selectedBlock.type !== 'divider'">
            <div class="lbe-style-clip">
              <button class="lbe-clip-btn" @click="copyStyle(selectedBlock.props)" title="複製此區塊的樣式">📋 複製樣式</button>
              <button class="lbe-clip-btn" :disabled="!copiedStyle" @click="pasteStyle(selectedBlock.props)" title="將複製的樣式套用到此區塊">📥 貼上樣式</button>
            </div>
            <button class="lbe-adv-open" @click="showAdv = true">
              <span>⚙ 進階樣式</span>
              <span v-if="hasAdv(selectedBlock.props)" class="lbe-adv-dot" title="已套用進階樣式">●</span>
            </button>
          </template>
        </template>
      </div>

      <!-- 右：即時預覽 -->
      <div class="lbe-preview-panel">
        <div class="lbe-preview-wrap" :class="'device-' + previewDevice">
          <iframe
            ref="previewFrame"
            class="lbe-iframe"
            sandbox="allow-scripts"
          ></iframe>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onUnmounted } from 'vue'
import { VueDraggableNext as draggable } from 'vue-draggable-next'
import axios from 'axios'
import { ADMIN_API } from '../store/app.js'

const props = defineProps({
  pageId: { type: Number, required: true },
  pageTitle: { type: String, default: '' },
  pageSlug: { type: String, default: '' },
  initialBlocks: { type: Array, default: () => [] },
  variantId: { type: Number, default: null }  // if set, save to variant instead of main blocks
})
const emit = defineEmits(['close', 'saved'])

const token = () => localStorage.getItem('token') || ''
const authH = () => ({ Authorization: `Bearer ${token()}` })

// ── State ──────────────────────────────────────────────────────────────────────
const blocks = ref([])
const selectedId = ref(null)
const saving = ref(false)
const previewDevice = ref('desktop')
const previewFrame = ref(null)
let _previewTimer = null

// ── Undo / Redo ────────────────────────────────────────────────────────────────
const _hist = []
let _histIdx = -1
let _histTimer = null
let _skipHist = false
const canUndo = ref(false)
const canRedo = ref(false)

function _pushHistory() {
  if (_skipHist) return
  const snap = JSON.stringify(blocks.value)
  _hist.splice(_histIdx + 1)
  _hist.push(snap)
  if (_hist.length > 60) _hist.shift()
  _histIdx = _hist.length - 1
  canUndo.value = _histIdx > 0
  canRedo.value = false
}

function _scheduleHistory() {
  clearTimeout(_histTimer)
  _histTimer = setTimeout(_pushHistory, 1200)
}

function undo() {
  if (_histIdx <= 0) return
  _histIdx--
  _skipHist = true
  blocks.value = JSON.parse(_hist[_histIdx])
  _skipHist = false
  canUndo.value = _histIdx > 0
  canRedo.value = true
  hasUnsaved.value = true
  updatePreview()
}

function redo() {
  if (_histIdx >= _hist.length - 1) return
  _histIdx++
  _skipHist = true
  blocks.value = JSON.parse(_hist[_histIdx])
  _skipHist = false
  canUndo.value = _histIdx > 0
  canRedo.value = _histIdx < _hist.length - 1
  hasUnsaved.value = true
  updatePreview()
}

function _onKeydown(e) {
  if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'z') {
    e.preventDefault(); undo()
  } else if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === 'y' || (e.shiftKey && e.key.toLowerCase() === 'z'))) {
    e.preventDefault(); redo()
  }
}

// ── Auto-save & Unsaved flag ───────────────────────────────────────────────────
const hasUnsaved = ref(false)
const autoSaveStatus = ref('')
let _autoSaveInterval = null

async function _autoSave() {
  if (!hasUnsaved.value || saving.value || props.variantId) return
  autoSaveStatus.value = 'saving'
  try {
    await axios.put(
      `${ADMIN_API}/ai/marketing/landing/${props.pageId}/blocks`,
      { blocks: blocks.value },
      { headers: authH() }
    )
    _saveLocalVersion()
    hasUnsaved.value = false
    autoSaveStatus.value = 'saved'
    setTimeout(() => { if (autoSaveStatus.value === 'saved') autoSaveStatus.value = '' }, 3000)
  } catch {
    autoSaveStatus.value = 'error'
    setTimeout(() => { if (autoSaveStatus.value === 'error') autoSaveStatus.value = '' }, 5000)
  }
}

// ── Version History ────────────────────────────────────────────────────────────
const showVersions = ref(false)
const localVersions = ref([])

function _saveLocalVersion() {
  try {
    const key = `lbe_v_${props.pageId}`
    const versions = JSON.parse(localStorage.getItem(key) || '[]')
    versions.unshift({ ts: Date.now(), blocks: JSON.stringify(blocks.value) })
    versions.splice(10)
    localStorage.setItem(key, JSON.stringify(versions))
  } catch {}
}

function loadLocalVersions() {
  try {
    const key = `lbe_v_${props.pageId}`
    localVersions.value = JSON.parse(localStorage.getItem(key) || '[]')
  } catch { localVersions.value = [] }
}

function restoreVersion(v) {
  if (!confirm('確定要恢復此版本？目前未儲存的變更將會遺失。')) return
  _skipHist = true
  blocks.value = JSON.parse(v.blocks)
  _skipHist = false
  _pushHistory()
  hasUnsaved.value = true
  showVersions.value = false
  updatePreview()
}

function formatVersionTime(ts) {
  const diff = Date.now() - ts
  if (diff < 60000) return '剛才'
  if (diff < 3600000) return `${Math.floor(diff / 60000)} 分鐘前`
  if (diff < 86400000) return `${Math.floor(diff / 3600000)} 小時前`
  const d = new Date(ts)
  return d.toLocaleDateString('zh-TW') + ' ' + d.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })
}

// ── Image Upload ───────────────────────────────────────────────────────────────
const imgUploading = ref(false)
const imgError = ref('')

async function _uploadImageData(data, contentType, callback) {
  imgUploading.value = true
  imgError.value = ''
  try {
    const b64 = await new Promise((res, rej) => {
      const reader = new FileReader()
      reader.onload = () => res(reader.result)
      reader.onerror = rej
      reader.readAsDataURL(new Blob([data], { type: contentType }))
    })
    const resp = await axios.post(
      `${ADMIN_API}/ai/marketing/landing/upload-image-base64`,
      { data_url: b64 },
      { headers: authH() }
    )
    callback(resp.data.url)
  } catch (e) {
    imgError.value = e.response?.data?.detail || '上傳失敗'
    setTimeout(() => imgError.value = '', 4000)
  } finally {
    imgUploading.value = false
  }
}

async function uploadImageFile(event, callback) {
  const file = event.target.files?.[0]
  if (!file) return
  event.target.value = ''
  imgUploading.value = true
  imgError.value = ''
  try {
    const fd = new FormData()
    fd.append('file', file)
    const resp = await axios.post(
      `${ADMIN_API}/ai/marketing/landing/upload-image`,
      fd,
      { headers: { ...authH(), 'Content-Type': 'multipart/form-data' } }
    )
    callback(resp.data.url)
  } catch (e) {
    imgError.value = e.response?.data?.detail || '上傳失敗'
    setTimeout(() => imgError.value = '', 4000)
  } finally {
    imgUploading.value = false
  }
}

async function pasteImage(event, callback) {
  const items = event.clipboardData?.items || []
  for (const item of items) {
    if (item.type.startsWith('image/')) {
      const file = item.getAsFile()
      if (!file) continue
      const buf = await file.arrayBuffer()
      await _uploadImageData(new Uint8Array(buf), item.type, callback)
      return
    }
  }
  // fallback: plain text URL
  const text = event.clipboardData?.getData('text') || ''
  if (text.match(/^https?:\/\/.+\.(png|jpg|jpeg|gif|webp|svg)(\?.*)?$/i)) {
    callback(text)
  }
}

async function dropImage(event, callback) {
  const file = event.dataTransfer?.files?.[0]
  if (file && file.type.startsWith('image/')) {
    const buf = await file.arrayBuffer()
    await _uploadImageData(new Uint8Array(buf), file.type, callback)
    return
  }
  const text = event.dataTransfer?.getData('text') || ''
  if (text.match(/^https?:\/\//)) callback(text)
}

// ── Page Settings ─────────────────────────────────────────────────────────────
const showSettings = ref(false)
const settingsSaving = ref(false)
const settings = ref({ status: 'published', slug: props.pageSlug, meta_title: '', meta_desc: '', og_image_url: '', thank_you_type: 'message', thank_you_content: '', notify_email: '', webhook_url: '', custom_domain: '', auto_reply_subject: '', auto_reply_body: '', crm_create: false, ga4_id: '', fb_pixel_id: '', head_code: '' })

// ── 全站主題 (P3-1) ──────────────────────────────────────────────────────────────
const showTheme = ref(false)
const defaultTheme = () => ({ preset: 'default', primary: '#4f46e5', secondary: '#6366f1', accent: '#f59e0b', heading_font: 'system', body_font: 'system', radius: 'md', btn_style: 'solid' })
const theme = ref(defaultTheme())

const THEME_PRESETS = {
  default: { primary: '#4f46e5', secondary: '#6366f1', accent: '#f59e0b', name: '預設靛藍' },
  tech:    { primary: '#2563eb', secondary: '#0ea5e9', accent: '#06b6d4', name: '科技藍' },
  vibrant: { primary: '#ea580c', secondary: '#f97316', accent: '#facc15', name: '活力橙' },
  mono:    { primary: '#111827', secondary: '#374151', accent: '#6b7280', name: '極簡黑白' },
  nature:  { primary: '#16a34a', secondary: '#22c55e', accent: '#84cc16', name: '自然綠' },
  elegant: { primary: '#7c3aed', secondary: '#a855f7', accent: '#ec4899', name: '優雅紫' },
}
const FONT_STACKS = {
  system:    `-apple-system,BlinkMacSystemFont,'Noto Sans TC','Segoe UI',sans-serif`,
  'noto-sans':  `'Noto Sans TC',sans-serif`,
  'noto-serif': `'Noto Serif TC',serif`,
  rounded:   `'M PLUS Rounded 1c','Noto Sans TC',sans-serif`,
  mono:      `'JetBrains Mono','Noto Sans TC',monospace`,
}
const FONT_GOOGLE = {
  'noto-sans':  'Noto+Sans+TC:wght@400;500;700;800;900',
  'noto-serif': 'Noto+Serif+TC:wght@400;500;700;900',
  rounded:   'M+PLUS+Rounded+1c:wght@400;500;700;800',
  mono:      'JetBrains+Mono:wght@400;500;700',
}
const FONT_OPTIONS = [
  { v: 'system', label: '系統預設（黑體）' },
  { v: 'noto-sans', label: 'Noto Sans TC（思源黑體）' },
  { v: 'noto-serif', label: 'Noto Serif TC（思源宋體）' },
  { v: 'rounded', label: 'M PLUS Rounded（圓體）' },
  { v: 'mono', label: 'JetBrains Mono（等寬）' },
]
const RADIUS_MAP = { none: '0px', sm: '4px', md: '8px', lg: '16px', pill: '999px' }
const RADIUS_OPTIONS = [
  { v: 'none', label: '直角' }, { v: 'sm', label: '小圓角' }, { v: 'md', label: '中圓角' },
  { v: 'lg', label: '大圓角' }, { v: 'pill', label: '膠囊' },
]
const BTN_STYLE_OPTIONS = [
  { v: 'solid', label: '實心' }, { v: 'outline', label: '外框' },
  { v: 'shadow', label: '陰影' }, { v: 'gradient', label: '漸層' },
]

const applyPreset = (key) => {
  const pr = THEME_PRESETS[key]; if (!pr) return
  theme.value.preset = key
  theme.value.primary = pr.primary
  theme.value.secondary = pr.secondary
  theme.value.accent = pr.accent
  onThemeChange()
}
const onThemeChange = () => { hasUnsaved.value = true; _scheduleHistory(); schedulePreview() }

// ── 進階樣式 state / helpers (P3-2) ──────────────────────────────────────────────
const showAdv = ref(false)
const ADV_KEYS = ['adv_anim', 'adv_bg_type', 'adv_grad_from', 'adv_grad_to', 'adv_grad_dir',
  'adv_bg_image', 'adv_bg_overlay', 'adv_pad_x', 'adv_max_w', 'adv_border_w',
  'adv_border_color', 'adv_shadow', 'adv_text_align', 'm_hidden', 'cols_mobile']
const hasAdv = (p) => !!p && ADV_KEYS.some(k => {
  const v = p[k]
  return v !== undefined && v !== '' && v !== null && v !== 'none' && !(k === 'adv_grad_dir' && v === 135)
})
const resetAdv = (p) => { if (!p) return; ADV_KEYS.forEach(k => { delete p[k] }); schedulePreview() }

// ── 複製 / 貼上樣式 (P3-4) ────────────────────────────────────────────────────────
const STYLE_KEYS = ['bg_color', 'text_color', 'accent_color', 'padding_y', ...ADV_KEYS]
const copiedStyle = ref(null)
const copyStyle = (p) => {
  if (!p) return
  const snap = {}
  STYLE_KEYS.forEach(k => { if (p[k] !== undefined) snap[k] = JSON.parse(JSON.stringify(p[k])) })
  copiedStyle.value = snap
  window.$toast?.success('已複製樣式', '可到其他區塊貼上')
}
const pasteStyle = (p) => {
  if (!p || !copiedStyle.value) return
  Object.entries(copiedStyle.value).forEach(([k, v]) => { p[k] = JSON.parse(JSON.stringify(v)) })
  schedulePreview()
  window.$toast?.success('已套用樣式', '')
}

// ── 對比度檢查 (P3-4) ─────────────────────────────────────────────────────────────
const _lum = (hex) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '')
  if (!m) return null
  const c = [m[1], m[2], m[3]].map(h => {
    let v = parseInt(h, 16) / 255
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2]
}
const contrastWarn = computed(() => {
  const b = selectedBlock.value
  if (!b) return ''
  const p = b.props || {}
  const tc = p.text_color || (['hero', 'cta', 'stats', 'countdown'].includes(b.type) ? '#ffffff' : '#1f2937')
  let bg = p.bg_color
  if (p.adv_bg_type === 'gradient') bg = p.adv_grad_from
  if (!bg || p.adv_bg_type === 'image') return ''
  const l1 = _lum(tc), l2 = _lum(bg)
  if (l1 === null || l2 === null) return ''
  const ratio = (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05)
  if (ratio < 3) return `文字與背景對比過低（${ratio.toFixed(1)}:1），建議至少 4.5:1 以利閱讀`
  if (ratio < 4.5) return `文字對比偏低（${ratio.toFixed(1)}:1），小字可能不易閱讀`
  return ''
})

// 產生主題 <head> 片段（與後端 _theme_style 同步），回傳 { head, bodyClass }
const themeStyle = () => {
  const t = theme.value || {}
  const primary = t.primary || '#4f46e5'
  const secondary = t.secondary || '#6366f1'
  const accent = t.accent || '#f59e0b'
  const radius = RADIUS_MAP[t.radius] || '8px'
  const hFont = FONT_STACKS[t.heading_font] || FONT_STACKS.system
  const bFont = FONT_STACKS[t.body_font] || FONT_STACKS.system
  const fams = []
  ;[t.heading_font, t.body_font].forEach(k => { const g = FONT_GOOGLE[k]; if (g && !fams.includes(g)) fams.push(g) })
  let link = ''
  if (fams.length) {
    link = `<link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin><link rel="stylesheet" href="https://fonts.googleapis.com/css2?${fams.map(f => 'family=' + f).join('&')}&display=swap">`
  }
  const style = `:root{--lp-primary:${primary};--lp-secondary:${secondary};--lp-accent:${accent};--lp-radius:${radius};--lp-font-heading:${hFont};--lp-font-body:${bFont};}body{font-family:var(--lp-font-body)}h1,h2,h3,h4,.lp-hero h1,.lp-stat-number,.lp-pricing-price{font-family:var(--lp-font-heading)}`
  return { head: `${link}<style>${style}</style>`, bodyClass: `lp-btn-${t.btn_style || 'solid'}` }
}

// CTA 按鈕：未指定顏色時沿用主題（與後端 _btn 同步）
const _btn = (href, text, color, textColor, extra = '') => {
  if (!text) return ''
  if (color) {
    return `<a href="${_esc(href||'#lp-form')}" class="lp-btn" data-c="1" style="background:${_esc(color)};color:${_esc(textColor||'#fff')};${extra}">${_esc(text)}</a>`
  }
  return `<a href="${_esc(href||'#lp-form')}" class="lp-btn"${extra?` style="${extra}"`:''}>${_esc(text)}</a>`
}

// 表單欄位渲染 (P4-1/4-2/4-3) — 與後端 _field_html 同步
const fieldHtml = (f) => {
  const name = _esc(f.name || '')
  const label = _esc(f.label || f.name || '')
  const ftype = f.type || 'text'
  const ph = _esc(f.placeholder || '')
  const reqAttr = f.required ? 'required' : ''
  const reqStar = f.required ? '<span class="lp-req">*</span>' : ''
  const help = f.help ? `<small class="lp-field-help">${_esc(f.help)}</small>` : ''
  const width = f.width === 'half' ? 'half' : 'full'
  const va = ['minlength', 'maxlength', 'pattern', 'min', 'max']
    .filter(a => f[a] !== undefined && f[a] !== '').map(a => `${a}="${_esc(String(f[a]))}"`).join(' ')
  let cattrs = ''
  if (f.show_if_field) cattrs = ` data-showif="${_esc(f.show_if_field)}" data-showval="${_esc(f.show_if_value || '')}" style="display:none"`
  const stepAttr = (f.step !== undefined && f.step !== '' && !isNaN(parseInt(f.step))) ? ` data-step="${parseInt(f.step)}"` : ''
  if (ftype === 'hidden') return `<input type="hidden" name="${name}" value="${_esc(f.default || '')}">`
  const wrap = `<div class="lp-field-wrap lp-field-${width}"${cattrs}${stepAttr}>`
  const labelH = `<label for="lp_${name}">${label}${reqStar}</label>`
  let inner
  if (ftype === 'textarea') inner = `<textarea id="lp_${name}" name="${name}" placeholder="${ph}" ${reqAttr} ${va}></textarea>`
  else if (ftype === 'select') {
    const opts = (f.options || []).map(o => `<option value="${_esc(o)}">${_esc(o)}</option>`).join('')
    inner = `<select id="lp_${name}" name="${name}" ${reqAttr}><option value="">請選擇…</option>${opts}</select>`
  } else if (ftype === 'radio' || ftype === 'checkbox-group') {
    const itype = ftype === 'radio' ? 'radio' : 'checkbox'
    const gname = ftype === 'radio' ? name : `${name}[]`
    const opts = (f.options || []).map(o => `<label class="lp-opt"><input type="${itype}" name="${gname}" value="${_esc(o)}"${(itype === 'radio' && f.required) ? ' required' : ''}> ${_esc(o)}</label>`).join('')
    inner = `<div class="lp-opts">${opts}</div>`
  } else if (ftype === 'consent') {
    let ltxt = label
    if (f.consent_link) ltxt = `${label} <a href="${_esc(f.consent_link)}" target="_blank" style="text-decoration:underline">詳情</a>`
    return `${wrap}<label class="lp-consent"><input type="checkbox" name="${name}" value="1" ${reqAttr}> ${ltxt}${reqStar}</label>${help}</div>`
  } else if (ftype === 'rating') {
    let stars = ''
    for (let i = 5; i >= 1; i--) stars += `<input type="radio" name="${name}" id="lp_${name}_${i}" value="${i}"${(f.required && i === 5) ? ' required' : ''}><label for="lp_${name}_${i}" title="${i}">★</label>`
    return `${wrap}${labelH}<div class="lp-rating">${stars}</div>${help}</div>`
  } else {
    inner = `<input type="${_esc(ftype)}" id="lp_${name}" name="${name}" placeholder="${ph}" ${reqAttr} ${va}>`
  }
  return `${wrap}${labelH}${inner}${help}</div>`
}

// 手機欄數覆寫 (P3-3) — 與後端 _colm 同步
const colm = (p) => {
  const v = parseInt(p && p.cols_mobile)
  return (!isNaN(v) && v) ? `--cols-m:${Math.max(1, Math.min(4, v))};` : ''
}

// 進階區塊樣式 (P3-2) — 與後端 _apply_adv 同步
const SHADOW_MAP = { sm: '0 1px 3px rgba(0,0,0,.1)', md: '0 6px 16px rgba(0,0,0,.12)', lg: '0 16px 40px rgba(0,0,0,.18)' }
const applyAdv = (html, p) => {
  if (!html || !p) return html
  const extra = []
  const bt = p.adv_bg_type
  if (bt === 'gradient' && (p.adv_grad_from || p.adv_grad_to)) {
    extra.push(`background:linear-gradient(${_esc(String(p.adv_grad_dir||'135'))}deg,${_esc(p.adv_grad_from||'#6366f1')},${_esc(p.adv_grad_to||'#8b5cf6')})`)
  } else if (bt === 'image' && p.adv_bg_image) {
    const ov = Math.max(0, Math.min(100, parseInt(p.adv_bg_overlay) || 0)) / 100
    extra.push(ov
      ? `background:linear-gradient(rgba(0,0,0,${ov}),rgba(0,0,0,${ov})),url('${_esc(p.adv_bg_image)}') center/cover`
      : `background:url('${_esc(p.adv_bg_image)}') center/cover`)
  }
  const _i = (v) => { const n = parseInt(v); return isNaN(n) ? null : n }
  const px = _i(p.adv_pad_x); if (px !== null) extra.push(`padding-left:${px}px;padding-right:${px}px`)
  const bw = _i(p.adv_border_w); if (bw) extra.push(`border:${bw}px solid ${_esc(p.adv_border_color||'#e5e7eb')}`)
  if (SHADOW_MAP[p.adv_shadow]) extra.push(`box-shadow:${SHADOW_MAP[p.adv_shadow]}`)
  if (['left','center','right'].includes(p.adv_text_align)) extra.push(`text-align:${p.adv_text_align}`)
  const classes = []
  if (p.adv_anim && p.adv_anim !== 'none') classes.push(`lp-anim-${p.adv_anim}`)
  if (p.m_hidden) classes.push('lp-hide-mobile')
  const cls = classes.join(' ')
  const mw = _i(p.adv_max_w)
  if (mw && html.includes('class="lp-container"')) html = html.replace('class="lp-container"', `class="lp-container" style="max-width:${mw}px"`)
  if (!extra.length && !cls) return html
  if (extra.length) {
    const m = html.match(/style="([^"]*)"/)
    if (m) html = html.replace(m[0], `style="${m[1]};${extra.join(';')}"`)
    else html = html.replace(/^(\s*<(?:section|div)\b)/, `$1 style="${extra.join(';')}"`)
  }
  if (cls) {
    html = html.replace(/^(\s*<(?:section|div)\b)([^>]*)>/, (full, t, attrs) => {
      attrs = attrs.includes('class="') ? attrs.replace('class="', `class="${cls} `) : ` class="${cls}"${attrs}`
      return `${t}${attrs}>`
    })
  }
  return html
}

// ── Block type definitions ──────────────────────────────────────────────────────
const blockTypes = [
  { type: 'hero',         icon: '🦸', name: 'Hero 橫幅' },
  { type: 'text',         icon: '📝', name: '文字段落' },
  { type: 'features',     icon: '⭐', name: '特色列表' },
  { type: 'image',        icon: '🖼️', name: '圖片' },
  { type: 'image_text',   icon: '🗂️', name: '圖文並排' },
  { type: 'cta',          icon: '🎯', name: 'CTA 區塊' },
  { type: 'form',         icon: '📋', name: '聯絡表單' },
  { type: 'testimonials', icon: '💬', name: '客戶見證' },
  { type: 'stats',        icon: '📊', name: '統計數字' },
  { type: 'pricing',      icon: '💰', name: '定價方案' },
  { type: 'faq',          icon: '❓', name: 'FAQ 折疊' },
  { type: 'logos',        icon: '🏷️', name: 'Logo 牆' },
  { type: 'video',        icon: '▶️', name: '影片嵌入' },
  { type: 'countdown',    icon: '⏱️', name: '倒數計時' },
  { type: 'divider',      icon: '➖', name: '分隔線' },
  { type: 'html',         icon: '💻', name: 'HTML 自訂' },
]

const typeIcon = (t) => blockTypes.find(b => b.type === t)?.icon || '□'
const typeName = (t) => blockTypes.find(b => b.type === t)?.name || t

// ── Default props per block type ────────────────────────────────────────────────
const defaultProps = {
  hero: {
    title: '引人注目的主標題', subtitle: '一句話說明您的核心價值',
    bg_color: '#1e293b', text_color: '#ffffff',
    cta_text: '立即行動', cta_url: '#lp-form',
    cta_color: '#4f46e5', cta_text_color: '#ffffff', padding_y: 80
  },
  text: {
    html: '<p>在這裡輸入您的內容文字…</p>',
    align: 'left', bg_color: '#ffffff', text_color: '#1f2937', padding_y: 48
  },
  features: {
    title: '我們的特色',
    columns: 3, bg_color: '#f9fafb', padding_y: 60,
    items: [
      { icon: '⚡', title: '快速', desc: '效能卓越，讓您的業務加速成長' },
      { icon: '🔒', title: '安全', desc: '多重防護，保障您的資料安全' },
      { icon: '🎯', title: '精準', desc: '精準觸及目標客群' }
    ]
  },
  image: {
    url: '', alt: '', max_width: 800,
    align: 'center', bg_color: '#ffffff', padding_y: 32
  },
  cta: {
    title: '準備好了嗎？', subtitle: '立即開始，免費體驗 14 天',
    btn_text: '免費試用', btn_url: '#lp-form',
    bg_color: '#4f46e5', text_color: '#ffffff',
    btn_color: '#ffffff', btn_text_color: '#4f46e5', padding_y: 60
  },
  form: {
    title: '聯絡我們', submit_text: '送出',
    success_message: '感謝您！我們將盡快與您聯繫。',
    bg_color: '#f9fafb', btn_color: '#4f46e5', padding_y: 60,
    fields: [
      { name: 'name',    label: '姓名',  type: 'text',  required: true,  placeholder: '請輸入姓名' },
      { name: 'email',   label: 'Email', type: 'email', required: true,  placeholder: 'you@example.com' },
      { name: 'phone',   label: '電話',  type: 'tel',   required: false, placeholder: '' },
      { name: 'message', label: '留言',  type: 'textarea', required: false, placeholder: '' }
    ]
  },
  divider: { height: 1, color: '#e5e7eb', style: 'solid', margin_y: 0 },
  html: { code: '', padding_y: 0 },
  testimonials: {
    title: '客戶怎麼說', columns: 2, bg_color: '#ffffff', text_color: '#1f2937', padding_y: 60,
    items: [
      { name: '王小明', role: '產品經理・某科技公司', quote: '這個產品大幅提升了我們團隊的工作效率，強烈推薦！', avatar: '', rating: 5 },
      { name: '林美玲', role: '行銷總監・品牌顧問公司', quote: '服務品質一流，回應速度快，讓我們的業務成長顯著。', avatar: '', rating: 5 },
    ]
  },
  video: { url: '', aspect: '16:9', max_width: 800, bg_color: '#000000', padding_y: 40, caption: '' },
  stats: {
    title: '數字說明一切', columns: 3, bg_color: '#1e293b', text_color: '#ffffff', accent_color: '#6366f1', padding_y: 60,
    items: [
      { number: '10,000', unit: '+', label: '服務客戶數' },
      { number: '98', unit: '%', label: '客戶滿意度' },
      { number: '5', unit: '年', label: '豐富產業經驗' },
    ]
  },
  pricing: {
    title: '選擇適合的方案', subtitle: '所有方案均包含 14 天免費試用', bg_color: '#f9fafb', padding_y: 60,
    items: [
      { name: '入門版', price: 'NT$299', unit: '/ 月', desc: '適合個人與小型團隊', highlighted: false, badge: '', cta_text: '立即開始', cta_url: '#lp-form', cta_color: '#4f46e5', features: ['5 位使用者', '10GB 儲存空間', '基本報表', 'Email 支援'] },
      { name: '專業版', price: 'NT$799', unit: '/ 月', desc: '適合成長中的企業', highlighted: true, badge: '最多人選擇', cta_text: '立即開始', cta_url: '#lp-form', cta_color: '#6366f1', features: ['無限使用者', '100GB 儲存空間', '進階報表', '優先支援', 'API 存取'] },
      { name: '企業版', price: '聯絡我們', unit: '', desc: '大型企業客製化需求', highlighted: false, badge: '', cta_text: '聯繫業務', cta_url: '#lp-form', cta_color: '#4f46e5', features: ['無限一切', '專屬客服', 'SLA 保證', '自訂整合', '現場教育訓練'] },
    ]
  },
  faq: {
    title: '常見問題', bg_color: '#ffffff', padding_y: 60,
    items: [
      { question: '如何開始使用？', answer: '只需點擊「立即開始」按鈕，填寫基本資料後即可免費試用 14 天，無需信用卡。' },
      { question: '支援哪些付款方式？', answer: '我們接受信用卡、轉帳及發票請款等多種付款方式，歡迎聯繫業務了解詳情。' },
      { question: '可以隨時取消嗎？', answer: '是的，您可以隨時取消訂閱，不收取任何違約金。取消後可繼續使用至當期結束。' },
    ]
  },
  logos: {
    title: '合作夥伴', bg_color: '#f9fafb', padding_y: 48, grayscale: true,
    items: [
      { url: '', alt: '合作廠商 A', link: '' },
      { url: '', alt: '合作廠商 B', link: '' },
      { url: '', alt: '合作廠商 C', link: '' },
    ]
  },
  image_text: {
    image_url: '', image_alt: '',
    title: '為什麼選擇我們？',
    html: '<p>我們提供業界最完整的解決方案，幫助您的企業快速成長。</p><p>超過 10,000 家企業信賴我們的服務。</p>',
    layout: 'image_left', bg_color: '#ffffff', text_color: '#1f2937', padding_y: 60,
    cta_text: '了解更多', cta_url: '#lp-form', cta_color: '#4f46e5', cta_text_color: '#ffffff'
  },
  countdown: {
    target_date: '', title: '優惠即將截止', subtitle: '把握機會，立即行動！',
    bg_color: '#1e293b', text_color: '#ffffff', accent_color: '#6366f1', padding_y: 60,
    expired_text: '活動已結束'
  },
}

// ── Computed ─────────────────────────────────────────────────────────────────────
const selectedBlock = computed(() => blocks.value.find(b => b.id === selectedId.value) || null)

// ── Actions ───────────────────────────────────────────────────────────────────────
const select = (block) => { selectedId.value = block.id }

const addBlock = (type) => {
  const id = `${type}-${Date.now()}`
  const newBlock = { id, type, props: JSON.parse(JSON.stringify(defaultProps[type] || {})) }
  blocks.value.push(newBlock)
  selectedId.value = id
  hasUnsaved.value = true
  _pushHistory()
  schedulePreview()
}

const removeBlock = (idx) => {
  const removed = blocks.value[idx]
  blocks.value.splice(idx, 1)
  if (selectedId.value === removed?.id) selectedId.value = blocks.value[blocks.value.length - 1]?.id || null
  hasUnsaved.value = true
  _pushHistory()
  schedulePreview()
}

const duplicateBlock = (idx) => {
  const orig = blocks.value[idx]
  const copy = { id: `${orig.type}-${Date.now()}`, type: orig.type, props: JSON.parse(JSON.stringify(orig.props)) }
  blocks.value.splice(idx + 1, 0, copy)
  selectedId.value = copy.id
  hasUnsaved.value = true
  _pushHistory()
  schedulePreview()
}

const onDragEnd = () => {
  hasUnsaved.value = true
  _pushHistory()
  schedulePreview()
}

const addFeatureItem = () => {
  if (!selectedBlock.value) return
  const items = selectedBlock.value.props.items
  items.push({ icon: '✨', title: '新特色', desc: '描述文字' })
  schedulePreview()
}
const removeFeatureItem = (i) => {
  selectedBlock.value.props.items.splice(i, 1)
  schedulePreview()
}

const addFormField = () => {
  if (!selectedBlock.value) return
  selectedBlock.value.props.fields.push({ name: 'field', label: '欄位', type: 'text', required: false, placeholder: '', width: 'full' })
  schedulePreview()
}
const removeFormField = (i) => {
  selectedBlock.value.props.fields.splice(i, 1)
  schedulePreview()
}

// ── Preview renderer (mirrors backend Python logic) ─────────────────────────────
const _LP_CSS = `*{box-sizing:border-box;margin:0;padding:0}body{font-family:-apple-system,BlinkMacSystemFont,'Noto Sans TC','Segoe UI',sans-serif;color:#1f2937;line-height:1.6}img{max-width:100%;height:auto;display:block}a{color:inherit;text-decoration:none}.lp-container{max-width:1140px;margin:0 auto;padding:0 24px}.lp-btn{display:inline-block;padding:14px 32px;border-radius:var(--lp-radius,8px);font-size:1rem;font-weight:600;cursor:pointer;border:none;transition:opacity .2s,transform .15s,box-shadow .2s;text-decoration:none}.lp-btn:hover{opacity:.9;transform:translateY(-1px)}.lp-btn-solid .lp-btn:not([data-c]){background:var(--lp-primary);color:#fff}.lp-btn-outline .lp-btn:not([data-c]){background:transparent;border:2px solid var(--lp-primary);color:var(--lp-primary)}.lp-btn-shadow .lp-btn:not([data-c]){background:var(--lp-primary);color:#fff;box-shadow:0 8px 20px -6px var(--lp-primary)}.lp-btn-gradient .lp-btn:not([data-c]){background:linear-gradient(135deg,var(--lp-primary),var(--lp-secondary));color:#fff}.lp-hero{padding:80px 24px;text-align:center}.lp-hero h1{font-size:clamp(1.8rem,5vw,3.2rem);font-weight:800;line-height:1.2;margin-bottom:1rem}.lp-hero p{font-size:1.15rem;margin-bottom:2rem;opacity:.88;max-width:600px;margin-left:auto;margin-right:auto}.lp-text{padding:48px 24px}.lp-text .lp-container{max-width:720px}.lp-text-inner{font-size:1rem;line-height:1.8}.lp-text-inner h1,.lp-text-inner h2,.lp-text-inner h3{margin:1rem 0 .5rem;font-weight:700}.lp-text-inner p{margin-bottom:.75rem}.lp-text-inner ul,.lp-text-inner ol{margin-left:1.5rem;margin-bottom:.75rem}.lp-features{padding:60px 24px}.lp-features h2{text-align:center;font-size:1.9rem;font-weight:700;margin-bottom:2.5rem}.lp-features-grid{display:grid;gap:2rem}.lp-feature-item{text-align:center;padding:1.5rem}.lp-feature-icon{font-size:2.5rem;margin-bottom:.75rem}.lp-feature-title{font-size:1.05rem;font-weight:600;margin-bottom:.4rem}.lp-feature-desc{font-size:.9rem;opacity:.75}.lp-image{padding:32px 24px}.lp-cta{padding:60px 24px;text-align:center}.lp-cta h2{font-size:1.9rem;font-weight:700;margin-bottom:.6rem}.lp-cta p{font-size:1.05rem;margin-bottom:2rem;opacity:.88}.lp-form-section{padding:60px 24px}.lp-form-section h2{font-size:1.7rem;font-weight:700;margin-bottom:1.5rem;text-align:center}.lp-form{max-width:560px;margin:0 auto;display:grid;grid-template-columns:1fr 1fr;gap:1rem}.lp-field-wrap{display:flex;flex-direction:column}.lp-field-full{grid-column:1/-1}.lp-field-half{grid-column:span 1}.lp-form label{font-size:.9rem;font-weight:500;display:block;margin-bottom:4px}.lp-form input:not([type=radio]):not([type=checkbox]),.lp-form textarea,.lp-form select{width:100%;padding:10px 14px;border:1.5px solid #d1d5db;border-radius:var(--lp-radius,8px);font-size:.95rem;font-family:inherit;outline:none;transition:border-color .2s,box-shadow .2s;background:#fff}.lp-form input:focus,.lp-form textarea:focus,.lp-form select:focus{border-color:var(--lp-primary,#4f46e5)}.lp-form input:invalid:not(:placeholder-shown){border-color:#f87171}.lp-form textarea{min-height:100px;resize:vertical}.lp-field-help{font-size:.76rem;color:#9ca3af;margin-top:4px}.lp-req{color:#ef4444;margin-left:2px}.lp-opts{display:flex;flex-direction:column;gap:.4rem;padding-top:2px}.lp-opt{display:flex;align-items:center;gap:.5rem;font-size:.9rem;font-weight:400;cursor:pointer}.lp-opt input{width:auto;margin:0}.lp-consent{display:flex;align-items:flex-start;gap:.5rem;font-size:.85rem;font-weight:400;cursor:pointer;line-height:1.5}.lp-consent input{width:auto;margin-top:3px;flex-shrink:0}.lp-rating{display:inline-flex;flex-direction:row-reverse;justify-content:flex-end;gap:2px;font-size:1.8rem}.lp-rating input{display:none}.lp-rating label{color:#d1d5db;cursor:pointer;transition:color .15s;margin:0}.lp-rating label:hover,.lp-rating label:hover ~ label,.lp-rating input:checked ~ label{color:#f59e0b}.lp-form-submit{width:100%}.lp-form-nav{grid-column:1/-1;display:flex;gap:.75rem}.lp-form-nav .lp-btn{flex:1}.lp-form-progress{max-width:560px;margin:0 auto 1.5rem}.lp-form-progress-bar{height:6px;background:#e5e7eb;border-radius:999px;overflow:hidden}.lp-form-progress-fill{height:100%;width:0;background:var(--lp-primary,#4f46e5);transition:width .3s}.lp-step-dots{display:flex;justify-content:space-between;margin-top:6px}.lp-step-dot{width:10px;height:10px;border-radius:50%;background:#e5e7eb;transition:background .3s}.lp-step-dot.active{background:var(--lp-primary,#4f46e5)}.lp-form-success{display:none;text-align:center;padding:1.5rem;background:#ecfdf5;border:1.5px solid #6ee7b7;border-radius:8px;color:#065f46;font-weight:500;grid-column:1/-1}.lp-testimonials{padding:60px 24px}.lp-testimonials h2{text-align:center;font-size:1.9rem;font-weight:700;margin-bottom:2.5rem}.lp-testimonials-grid{display:grid;gap:1.5rem}.lp-testimonial-card{background:#fff;border:1px solid #e5e7eb;border-radius:12px;padding:1.5rem;display:flex;flex-direction:column;gap:.75rem}.lp-testimonial-quote{font-size:.95rem;line-height:1.7;flex:1;color:#374151}.lp-testimonial-meta{display:flex;align-items:center;gap:.75rem;margin-top:.5rem}.lp-testimonial-avatar{width:44px;height:44px;border-radius:50%;object-fit:cover;flex-shrink:0}.lp-testimonial-avatar-placeholder{width:44px;height:44px;border-radius:50%;background:#6366f1;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:1rem;flex-shrink:0}.lp-testimonial-name{font-size:.9rem;font-weight:700}.lp-testimonial-role{font-size:.8rem;color:#6b7280}.lp-testimonial-stars{color:#f59e0b;font-size:.9rem;letter-spacing:.05em}.lp-video-section{padding:40px 24px}.lp-video-wrap{position:relative;overflow:hidden;border-radius:12px}.lp-video-wrap iframe{position:absolute;inset:0;width:100%;height:100%;border:none}.lp-video-caption{text-align:center;font-size:.85rem;color:#6b7280;margin-top:.75rem}.lp-stats{padding:60px 24px;text-align:center}.lp-stats h2{font-size:1.9rem;font-weight:700;margin-bottom:2.5rem}.lp-stats-grid{display:grid;gap:2rem}.lp-stat-number{font-size:clamp(2rem,6vw,3.5rem);font-weight:900;line-height:1;margin-bottom:.25rem}.lp-stat-label{font-size:.95rem;opacity:.75}.lp-pricing{padding:60px 24px}.lp-pricing h2{text-align:center;font-size:1.9rem;font-weight:700;margin-bottom:.5rem}.lp-pricing-sub{text-align:center;font-size:1rem;color:#6b7280;margin-bottom:2.5rem}.lp-pricing-grid{display:grid;gap:1.5rem;align-items:start}.lp-pricing-card{border:2px solid #e5e7eb;border-radius:16px;padding:2rem;background:#fff;position:relative}.lp-pricing-card.highlighted{border-color:var(--lp-primary,#6366f1);box-shadow:0 8px 32px rgba(99,102,241,.15)}.lp-pricing-badge{position:absolute;top:-12px;left:50%;transform:translateX(-50%);background:var(--lp-primary,#6366f1);color:#fff;font-size:.72rem;font-weight:700;padding:3px 14px;border-radius:20px;white-space:nowrap}.lp-pricing-name{font-size:1.1rem;font-weight:700;margin-bottom:.75rem}.lp-pricing-price{font-size:2.8rem;font-weight:900;line-height:1;margin-bottom:.25rem}.lp-pricing-price span{font-size:1rem;font-weight:400;color:#6b7280}.lp-pricing-desc{font-size:.85rem;color:#6b7280;margin-bottom:1.25rem}.lp-pricing-features{list-style:none;display:flex;flex-direction:column;gap:.6rem;margin-bottom:1.5rem}.lp-pricing-features li{font-size:.88rem;display:flex;align-items:flex-start;gap:.5rem}.lp-pricing-features li::before{content:'✓';color:#22c55e;font-weight:700;flex-shrink:0}.lp-faq{padding:60px 24px}.lp-faq h2{text-align:center;font-size:1.9rem;font-weight:700;margin-bottom:2.5rem}.lp-faq-list{max-width:720px;margin:0 auto;display:flex;flex-direction:column;gap:.75rem}.lp-faq-item{border:1.5px solid #e5e7eb;border-radius:10px;overflow:hidden}.lp-faq-question{width:100%;background:#fff;border:none;padding:1rem 1.25rem;font-size:.95rem;font-weight:600;text-align:left;cursor:pointer;display:flex;justify-content:space-between;align-items:center;gap:1rem;color:#111;font-family:inherit}.lp-faq-question:hover{background:#f9fafb}.lp-faq-icon{font-size:1.1rem;flex-shrink:0;transition:transform .25s;color:var(--lp-primary,#6366f1)}.lp-faq-answer{display:none;padding:0 1.25rem 1rem;font-size:.9rem;color:#374151;line-height:1.7}.lp-faq-item.open .lp-faq-answer{display:block}.lp-faq-item.open .lp-faq-icon{transform:rotate(45deg)}.lp-logos{padding:48px 24px}.lp-logos h2{text-align:center;font-size:1.1rem;font-weight:600;opacity:.5;margin-bottom:2rem;text-transform:uppercase;letter-spacing:.1em}.lp-logos-grid{display:flex;flex-wrap:wrap;gap:2rem;justify-content:center;align-items:center}.lp-logo-item{display:flex;align-items:center;justify-content:center;opacity:.55;transition:opacity .2s}.lp-logo-item:hover{opacity:1}.lp-logo-item img{max-height:48px;max-width:160px;width:auto;object-fit:contain}.lp-logo-item.grayscale img{filter:grayscale(1)}.lp-image-text{padding:60px 24px}.lp-it-grid{display:grid;grid-template-columns:1fr 1fr;gap:3rem;align-items:center}.lp-it-grid.img-right .lp-it-img{order:2}.lp-it-grid.img-right .lp-it-text{order:1}.lp-it-img img{width:100%;border-radius:12px}.lp-it-img-placeholder{background:#e5e7eb;border-radius:12px;height:280px;display:flex;align-items:center;justify-content:center;color:#9ca3af}.lp-it-text h2{font-size:1.7rem;font-weight:800;margin-bottom:1rem}.lp-it-text-inner{font-size:.95rem;line-height:1.8;margin-bottom:1.5rem}.lp-it-text-inner p{margin-bottom:.6rem}.lp-countdown{padding:60px 24px;text-align:center}.lp-countdown h2{font-size:1.9rem;font-weight:700;margin-bottom:.5rem}.lp-countdown p{font-size:1.05rem;opacity:.85;margin-bottom:2rem}.lp-countdown-timer{display:flex;justify-content:center;gap:1rem;flex-wrap:wrap}.lp-countdown-unit{min-width:80px}.lp-countdown-num{font-size:clamp(2.5rem,8vw,4rem);font-weight:900;line-height:1;display:block}.lp-countdown-label{font-size:.75rem;opacity:.65;text-transform:uppercase;letter-spacing:.1em;display:block;margin-top:.25rem}.lp-countdown-sep{font-size:3rem;font-weight:900;line-height:1;opacity:.4;align-self:flex-start;padding-top:.25rem}.lp-countdown-expired{font-size:1.5rem;font-weight:700;opacity:.7}.lp-anim-fade,.lp-anim-up,.lp-anim-zoom{opacity:0;transition:opacity .6s ease,transform .6s ease;will-change:opacity,transform}.lp-anim-up{transform:translateY(34px)}.lp-anim-zoom{transform:scale(.94)}.lp-anim-fade.in,.lp-anim-up.in,.lp-anim-zoom.in{opacity:1;transform:none}@media(max-width:768px){.lp-features-grid{grid-template-columns:repeat(var(--cols-m,1),1fr)!important}.lp-hero{padding:48px 16px}.lp-testimonials-grid{grid-template-columns:repeat(var(--cols-m,1),1fr)!important}.lp-stats-grid{grid-template-columns:repeat(var(--cols-m,2),1fr)!important}.lp-pricing-grid{grid-template-columns:repeat(var(--cols-m,1),1fr)!important}.lp-it-grid{grid-template-columns:1fr!important}.lp-countdown-unit{min-width:64px}.lp-hide-mobile{display:none!important}.lp-form{grid-template-columns:1fr}}`

const _esc = (s) => String(s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;')

const renderBlock = (b) => {
  const p = b.props || {}
  switch (b.type) {
    case 'hero': {
      const sub = p.subtitle ? `<p>${_esc(p.subtitle)}</p>` : ''
      const btn = _btn(p.cta_url, p.cta_text, p.cta_color, p.cta_text_color)
      return `<section class="lp-hero" style="background:${_esc(p.bg_color||'#1e293b')};color:${_esc(p.text_color||'#fff')};padding:${p.padding_y||80}px 24px;"><div class="lp-container"><h1>${_esc(p.title||'')}</h1>${sub}${btn}</div></section>`
    }
    case 'text':
      return `<section class="lp-text" style="background:${_esc(p.bg_color||'#fff')};padding:${p.padding_y||48}px 24px;"><div class="lp-container"><div class="lp-text-inner" style="color:${_esc(p.text_color||'#1f2937')};text-align:${_esc(p.align||'left')};">${p.html||''}</div></div></section>`
    case 'features': {
      const cols = Math.max(1, Math.min(4, p.columns || 3))
      const titleH = p.title ? `<h2>${_esc(p.title)}</h2>` : ''
      const items = (p.items||[]).map(it => `<div class="lp-feature-item"><div class="lp-feature-icon">${_esc(it.icon||'')}</div><div class="lp-feature-title">${_esc(it.title||'')}</div><div class="lp-feature-desc">${_esc(it.desc||'')}</div></div>`).join('')
      return `<section class="lp-features" style="background:${_esc(p.bg_color||'#f9fafb')};padding:${p.padding_y||60}px 24px;"><div class="lp-container">${titleH}<div class="lp-features-grid" style="grid-template-columns:repeat(${cols},1fr);${colm(p)}">${items}</div></div></section>`
    }
    case 'image': {
      const img = p.url
        ? `<img src="${_esc(p.url)}" alt="${_esc(p.alt||'')}" style="max-width:${p.max_width||800}px;width:100%;margin:${p.align==='center'?'0 auto':p.align==='right'?'0 0 0 auto':'0 auto 0 0'};">`
        : `<div style="height:120px;background:#e5e7eb;border-radius:8px;display:flex;align-items:center;justify-content:center;color:#9ca3af;">圖片未設定</div>`
      return `<section class="lp-image" style="background:${_esc(p.bg_color||'#fff')};padding:${p.padding_y||32}px 24px;"><div class="lp-container">${img}</div></section>`
    }
    case 'cta': {
      const sub = p.subtitle ? `<p>${_esc(p.subtitle)}</p>` : ''
      const btn = _btn(p.btn_url, p.btn_text, p.btn_color, p.btn_text_color)
      return `<section class="lp-cta" style="background:${_esc(p.bg_color||'#4f46e5')};color:${_esc(p.text_color||'#fff')};padding:${p.padding_y||60}px 24px;"><div class="lp-container"><h2>${_esc(p.title||'')}</h2>${sub}${btn}</div></section>`
    }
    case 'form': {
      const fieldsHtml = (p.fields||[]).map(fieldHtml).join('')
      const steps = [...new Set((p.fields||[]).filter(f => f.step !== undefined && f.step !== '' && !isNaN(parseInt(f.step))).map(f => parseInt(f.step)))].sort((a,b)=>a-b)
      const multistep = steps.length > 1
      const subDc = p.btn_color ? ' data-c="1"' : ''
      const subColor = p.btn_color ? `background:${_esc(p.btn_color)};color:#fff;` : ''
      let progressH = '', navH = '', formAttr = ''
      if (multistep) {
        const n = steps.length
        formAttr = ` data-steps="${n}"`
        const dots = Array.from({length:n}, (_,i)=>`<span class="lp-step-dot" data-step="${i}"></span>`).join('')
        progressH = `<div class="lp-form-progress"><div class="lp-form-progress-bar"><div class="lp-form-progress-fill" id="lp-progress-fill"></div></div><div class="lp-step-dots">${dots}</div></div>`
        navH = `<div class="lp-form-nav"><button type="button" class="lp-btn lp-form-prev" data-c="1" style="background:#e5e7eb;color:#374151;">上一步</button><button type="button" class="lp-btn lp-form-next">下一步</button><button type="submit" class="lp-btn lp-form-submit"${subDc} style="${subColor}display:none;">${_esc(p.submit_text||'送出')}</button></div>`
      } else {
        navH = `<button type="submit" class="lp-btn lp-form-submit"${subDc} style="${subColor}">${_esc(p.submit_text||'送出')}</button>`
      }
      return `<section class="lp-form-section" style="background:${_esc(p.bg_color||'#f9fafb')};padding:${p.padding_y||60}px 24px;"><div class="lp-container"><h2>${_esc(p.title||'聯絡我們')}</h2>${progressH}<form id="lp-form" class="lp-form"${formAttr}>${fieldsHtml}${navH}</form><div id="lp-form-success" class="lp-form-success">${_esc(p.success_message||'感謝您！')}</div></div></section>`
    }
    case 'divider':
      return `<div style="margin:${p.margin_y||0}px 0;"><hr style="border:none;border-top:${p.height||1}px ${_esc(p.style||'solid')} ${_esc(p.color||'#e5e7eb')};"></div>`
    case 'html':
      return p.padding_y ? `<div style="padding:${p.padding_y}px 24px;">${p.code||''}</div>` : (p.code||'')
    case 'testimonials': {
      const cols = Math.max(1, Math.min(3, p.columns || 2))
      const titleH = p.title ? `<h2>${_esc(p.title)}</h2>` : ''
      const cards = (p.items || []).map(item => {
        const initial = (item.name || 'U')[0].toUpperCase()
        const avatarHtml = item.avatar
          ? `<img src="${_esc(item.avatar)}" alt="${_esc(item.name||'')}" class="lp-testimonial-avatar">`
          : `<div class="lp-testimonial-avatar-placeholder">${initial}</div>`
        const rating = Math.min(5, Math.max(1, Number(item.rating) || 5))
        const stars = '★'.repeat(rating) + '☆'.repeat(5 - rating)
        return `<div class="lp-testimonial-card"><div class="lp-testimonial-stars">${stars}</div><div class="lp-testimonial-quote">「${_esc(item.quote||'')}」</div><div class="lp-testimonial-meta">${avatarHtml}<div><div class="lp-testimonial-name">${_esc(item.name||'')}</div><div class="lp-testimonial-role">${_esc(item.role||'')}</div></div></div></div>`
      }).join('')
      return `<section class="lp-testimonials" style="background:${_esc(p.bg_color||'#fff')};color:${_esc(p.text_color||'#1f2937')};padding:${p.padding_y||60}px 24px;"><div class="lp-container">${titleH}<div class="lp-testimonials-grid" style="grid-template-columns:repeat(${cols},1fr);${colm(p)}">${cards}</div></div></section>`
    }
    case 'video': {
      const bg = _esc(p.bg_color || '#000')
      const pad = p.padding_y || 40
      const url = p.url || ''
      const ratioMap = {'16:9': '56.25%', '4:3': '75%', '1:1': '100%'}
      const ratio = ratioMap[p.aspect] || '56.25%'
      let videoHtml = ''
      if (!url) {
        videoHtml = '<div style="height:200px;background:#374151;border-radius:12px;display:flex;align-items:center;justify-content:center;color:#9ca3af;font-size:2rem;">▶</div>'
      } else {
        const ytM = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/)
        const vmM = url.match(/vimeo\.com\/(\d+)/)
        if (ytM) videoHtml = `<div class="lp-video-wrap" style="padding-bottom:${ratio};"><iframe src="https://www.youtube.com/embed/${ytM[1]}" allowfullscreen></iframe></div>`
        else if (vmM) videoHtml = `<div class="lp-video-wrap" style="padding-bottom:${ratio};"><iframe src="https://player.vimeo.com/video/${vmM[1]}" allowfullscreen></iframe></div>`
        else videoHtml = `<video src="${_esc(url)}" controls style="width:100%;border-radius:12px;"></video>`
      }
      const captionHtml = p.caption ? `<p class="lp-video-caption">${_esc(p.caption)}</p>` : ''
      return `<section class="lp-video-section" style="background:${bg};padding:${pad}px 24px;"><div class="lp-container"><div style="max-width:${p.max_width||800}px;margin:0 auto;">${videoHtml}${captionHtml}</div></div></section>`
    }
    case 'stats': {
      const cols = Math.max(1, Math.min(4, p.columns || 3))
      const titleH = p.title ? `<h2>${_esc(p.title)}</h2>` : ''
      const ac = _esc(p.accent_color || '#6366f1')
      const items = (p.items || []).map(item =>
        `<div class="lp-stat-item"><div class="lp-stat-number" style="color:${ac};">${_esc(String(item.number||''))}${_esc(String(item.unit||''))}</div><div class="lp-stat-label">${_esc(item.label||'')}</div></div>`
      ).join('')
      return `<section class="lp-stats" style="background:${_esc(p.bg_color||'#1e293b')};color:${_esc(p.text_color||'#fff')};padding:${p.padding_y||60}px 24px;"><div class="lp-container">${titleH}<div class="lp-stats-grid" style="grid-template-columns:repeat(${cols},1fr);${colm(p)}">${items}</div></div></section>`
    }
    case 'pricing': {
      const titleH = p.title ? `<h2>${_esc(p.title)}</h2>` : ''
      const subH = p.subtitle ? `<p class="lp-pricing-sub">${_esc(p.subtitle)}</p>` : ''
      const items = p.items || []
      const cols = Math.min(items.length || 3, 3)
      const cards = items.map(item => {
        const badgeHtml = item.highlighted ? `<div class="lp-pricing-badge">${_esc(item.badge||'推薦方案')}</div>` : ''
        const features = (item.features || []).map(f => `<li>${_esc(f)}</li>`).join('')
        const priceBtn = _btn(item.cta_url, item.cta_text||'立即選擇', item.cta_color, '#fff', 'width:100%;text-align:center;display:block;')
        return `<div class="${item.highlighted ? 'lp-pricing-card highlighted' : 'lp-pricing-card'}">${badgeHtml}<div class="lp-pricing-name">${_esc(item.name||'')}</div><div class="lp-pricing-price">${_esc(String(item.price||''))}<span>${_esc(item.unit||'/ 月')}</span></div><div class="lp-pricing-desc">${_esc(item.desc||'')}</div><ul class="lp-pricing-features">${features}</ul>${priceBtn}</div>`
      }).join('')
      return `<section class="lp-pricing" style="background:${_esc(p.bg_color||'#f9fafb')};padding:${p.padding_y||60}px 24px;"><div class="lp-container">${titleH}${subH}<div class="lp-pricing-grid" style="grid-template-columns:repeat(${cols},1fr);${colm(p)}">${cards}</div></div></section>`
    }
    case 'faq': {
      const titleH = p.title ? `<h2>${_esc(p.title)}</h2>` : ''
      const items = (p.items || []).map((item, i) =>
        `<div class="lp-faq-item" id="faq-${i}"><button class="lp-faq-question" onclick="(function(el){el.classList.toggle('open')})(this.closest('.lp-faq-item'))">${_esc(item.question||'')}<span class="lp-faq-icon">+</span></button><div class="lp-faq-answer">${_esc(item.answer||'')}</div></div>`
      ).join('')
      return `<section class="lp-faq" style="background:${_esc(p.bg_color||'#fff')};padding:${p.padding_y||60}px 24px;"><div class="lp-container">${titleH}<div class="lp-faq-list">${items}</div></div></section>`
    }
    case 'logos': {
      const titleH = p.title ? `<h2>${_esc(p.title)}</h2>` : ''
      const gsClass = p.grayscale !== false ? ' grayscale' : ''
      const items = (p.items || []).map(item => {
        const imgTag = item.url ? `<img src="${_esc(item.url)}" alt="${_esc(item.alt||'')}">` : `<div style="width:120px;height:48px;background:#e5e7eb;border-radius:6px;"></div>`
        const inner = item.link ? `<a href="${_esc(item.link)}" target="_blank">${imgTag}</a>` : imgTag
        return `<div class="lp-logo-item${gsClass}">${inner}</div>`
      }).join('')
      return `<section class="lp-logos" style="background:${_esc(p.bg_color||'#f9fafb')};padding:${p.padding_y||48}px 24px;"><div class="lp-container">${titleH}<div class="lp-logos-grid">${items}</div></div></section>`
    }
    case 'image_text': {
      const imgHtml = p.image_url ? `<img src="${_esc(p.image_url)}" alt="${_esc(p.image_alt||'')}">` : '<div class="lp-it-img-placeholder">圖片未設定</div>'
      const titleH = p.title ? `<h2>${_esc(p.title)}</h2>` : ''
      const ctaHtml = _btn(p.cta_url, p.cta_text, p.cta_color, p.cta_text_color)
      const gridClass = p.layout === 'image_right' ? 'lp-it-grid img-right' : 'lp-it-grid'
      return `<section class="lp-image-text" style="background:${_esc(p.bg_color||'#fff')};color:${_esc(p.text_color||'#1f2937')};padding:${p.padding_y||60}px 24px;"><div class="lp-container"><div class="${gridClass}"><div class="lp-it-img">${imgHtml}</div><div class="lp-it-text">${titleH}<div class="lp-it-text-inner">${p.html||''}</div>${ctaHtml}</div></div></div></section>`
    }
    case 'countdown': {
      const bg = _esc(p.bg_color || '#1e293b')
      const tc = _esc(p.text_color || '#fff')
      const ac = _esc(p.accent_color || '#6366f1')
      const titleH = p.title ? `<h2>${_esc(p.title)}</h2>` : ''
      const subH = p.subtitle ? `<p>${_esc(p.subtitle)}</p>` : ''
      const uid = b.id.replace(/[^a-zA-Z0-9]/g, '_')
      const expText = _esc(p.expired_text || '活動已結束')
      const timerHtml = `<div class="lp-countdown-timer" id="${uid}"><div class="lp-countdown-unit"><span class="lp-countdown-num" id="${uid}-d">--</span><span class="lp-countdown-label">天</span></div><div class="lp-countdown-sep" style="color:${ac};">:</div><div class="lp-countdown-unit"><span class="lp-countdown-num" id="${uid}-h">--</span><span class="lp-countdown-label">時</span></div><div class="lp-countdown-sep" style="color:${ac};">:</div><div class="lp-countdown-unit"><span class="lp-countdown-num" id="${uid}-m">--</span><span class="lp-countdown-label">分</span></div><div class="lp-countdown-sep" style="color:${ac};">:</div><div class="lp-countdown-unit"><span class="lp-countdown-num" id="${uid}-s">--</span><span class="lp-countdown-label">秒</span></div></div><div class="lp-countdown-expired" id="${uid}-exp" style="display:none;">${expText}</div>`
      const js = p.target_date ? `<scr`+`ipt>(function(){var end=new Date("${p.target_date}").getTime();function tick(){var now=Date.now(),diff=end-now;if(diff<=0){document.getElementById("${uid}").style.display="none";document.getElementById("${uid}-exp").style.display="";return;}var d=Math.floor(diff/864e5),h=Math.floor((diff%864e5)/36e5),m=Math.floor((diff%36e5)/6e4),s=Math.floor((diff%6e4)/1e3);["d","h","m","s"].forEach(function(k,i){var v=[d,h,m,s][i];document.getElementById("${uid}-"+k).textContent=String(v).padStart(2,"0");});setTimeout(tick,1e3);}tick();})();<\/scr`+`ipt>` : ''
      return `<section class="lp-countdown" style="background:${bg};color:${tc};padding:${p.padding_y||60}px 24px;"><div class="lp-container">${titleH}${subH}${timerHtml}</div></section>${js}`
    }
    default:
      return ''
  }
}

const _ANIM_JS = `<script>(function(){var els=document.querySelectorAll('.lp-anim-fade,.lp-anim-up,.lp-anim-zoom');if(!('IntersectionObserver' in window)){els.forEach(function(e){e.classList.add('in')});return;}var o=new IntersectionObserver(function(en){en.forEach(function(x){if(x.isIntersecting){x.target.classList.add('in');o.unobserve(x.target);}})},{threshold:.12});els.forEach(function(e){o.observe(e)});})();<\/script>`

const buildPreviewHtml = () => {
  const body = blocks.value.map(b => applyAdv(renderBlock(b), b.props || {})).join('\n')
  const th = themeStyle()
  return `<!DOCTYPE html><html lang="zh-TW"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"><title>${_esc(props.pageTitle)}</title><style>${_LP_CSS}</style>${th.head}</head><body class="${th.bodyClass}">${body}${_ANIM_JS}</body></html>`
}

const updatePreview = () => {
  if (!previewFrame.value) return
  previewFrame.value.srcdoc = buildPreviewHtml()
}

const schedulePreview = () => {
  clearTimeout(_previewTimer)
  _previewTimer = setTimeout(updatePreview, 350)
  if (!_skipHist) {
    hasUnsaved.value = true
    _scheduleHistory()
  }
}

// ── Load blocks from API ─────────────────────────────────────────────────────────
const load = async () => {
  try {
    const [blocksRes, pageRes] = await Promise.all([
      axios.get(`${ADMIN_API}/ai/marketing/landing/${props.pageId}/blocks`, { headers: authH() }),
      axios.get(`${ADMIN_API}/ai/marketing/landing/${props.pageId}`, { headers: authH() })
    ])
    const res = blocksRes
    const pageData = pageRes.data
    settings.value = {
      status: pageData.status || 'published',
      slug: pageData.slug || props.pageSlug,
      meta_title: pageData.meta_title || '',
      meta_desc: pageData.meta_desc || '',
      og_image_url: pageData.og_image_url || '',
      thank_you_type: pageData.thank_you_type || 'message',
      thank_you_content: pageData.thank_you_content || '',
      notify_email: pageData.notify_email || '',
      webhook_url: pageData.webhook_url || '',
      custom_domain: pageData.custom_domain || '',
      auto_reply_subject: pageData.auto_reply_subject || '',
      auto_reply_body: pageData.auto_reply_body || '',
      crm_create: !!pageData.crm_create,
      ga4_id: pageData.ga4_id || '',
      fb_pixel_id: pageData.fb_pixel_id || '',
      head_code: pageData.head_code || ''
    }
    // 載入全站主題
    try {
      theme.value = pageData.theme_json
        ? { ...defaultTheme(), ...JSON.parse(pageData.theme_json) }
        : defaultTheme()
    } catch { theme.value = defaultTheme() }
    const raw = res.data.blocks || []
    if (raw.length > 0) {
      blocks.value = raw
    } else if (props.initialBlocks && props.initialBlocks.length > 0) {
      // Use template blocks passed from parent, stamp new IDs
      blocks.value = props.initialBlocks.map((b, i) => ({
        ...b,
        id: `${b.type}-${Date.now()}-${i}`,
        props: JSON.parse(JSON.stringify(b.props || {}))
      }))
    } else {
      // Fallback: minimal hero + form
      blocks.value = [
        { id: `hero-${Date.now()}`, type: 'hero', props: JSON.parse(JSON.stringify(defaultProps.hero)) },
        { id: `form-${Date.now()+1}`, type: 'form', props: JSON.parse(JSON.stringify(defaultProps.form)) }
      ]
      blocks.value[0].props.title = props.pageTitle || '歡迎'
    }
    selectedId.value = blocks.value[0]?.id || null
    await nextTick()
    updatePreview()
  } catch (e) {
    window.$toast?.error('載入區塊失敗', e.message)
  }
}

// ── Save ─────────────────────────────────────────────────────────────────────────
const save = async () => {
  saving.value = true
  try {
    if (props.variantId) {
      // Save to A/B variant
      await axios.put(
        `${ADMIN_API}/ai/marketing/landing/${props.pageId}/variants/${props.variantId}`,
        { blocks_json: JSON.stringify(blocks.value) },
        { headers: authH() }
      )
      window.$toast?.success('已儲存', '變體區塊已更新')
    } else {
      // 先存主題（區塊端會以已存主題重新渲染），再存區塊
      await axios.put(
        `${ADMIN_API}/ai/marketing/landing/${props.pageId}`,
        { theme_json: JSON.stringify(theme.value) },
        { headers: authH() }
      )
      await axios.put(
        `${ADMIN_API}/ai/marketing/landing/${props.pageId}/blocks`,
        { blocks: blocks.value },
        { headers: authH() }
      )
      window.$toast?.success('已儲存', '推廣頁區塊已更新')
    }
    hasUnsaved.value = false
    _saveLocalVersion()
    emit('saved')
  } catch (e) {
    window.$toast?.error('儲存失敗', e.response?.data?.detail || e.message)
  } finally {
    saving.value = false
  }
}

const saveSettings = async () => {
  settingsSaving.value = true
  try {
    await axios.put(
      `${ADMIN_API}/ai/marketing/landing/${props.pageId}`,
      {
        status: settings.value.status,
        slug: settings.value.slug.trim() || undefined,
        meta_title: settings.value.meta_title,
        meta_desc: settings.value.meta_desc,
        og_image_url: settings.value.og_image_url,
        thank_you_type: settings.value.thank_you_type,
        thank_you_content: settings.value.thank_you_content,
        notify_email: settings.value.notify_email,
        webhook_url: settings.value.webhook_url,
        custom_domain: settings.value.custom_domain,
        auto_reply_subject: settings.value.auto_reply_subject,
        auto_reply_body: settings.value.auto_reply_body,
        crm_create: settings.value.crm_create,
        ga4_id: settings.value.ga4_id,
        fb_pixel_id: settings.value.fb_pixel_id,
        head_code: settings.value.head_code
      },
      { headers: authH() }
    )
    window.$toast?.success('已儲存', '頁面設定已更新')
    showSettings.value = false
  } catch (e) {
    window.$toast?.error('儲存失敗', e.response?.data?.detail || e.message)
  } finally {
    settingsSaving.value = false
  }
}

onMounted(async () => {
  await load()
  _pushHistory()
  window.addEventListener('keydown', _onKeydown)
  _autoSaveInterval = setInterval(_autoSave, 30000)
})

onUnmounted(() => {
  window.removeEventListener('keydown', _onKeydown)
  clearInterval(_autoSaveInterval)
  clearTimeout(_histTimer)
  clearTimeout(_previewTimer)
})
</script>

<style scoped>
.lbe-root {
  position: fixed;
  inset: 0;
  z-index: 200;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
}

/* Header */
.lbe-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 16px;
  height: 52px;
  background: #1e293b;
  color: #fff;
  flex-shrink: 0;
  border-bottom: 1px solid #334155;
}
.lbe-back-btn {
  background: rgba(255,255,255,.1);
  border: none;
  color: #fff;
  padding: 6px 14px;
  border-radius: 6px;
  cursor: pointer;
  font-size: .85rem;
  white-space: nowrap;
  transition: background .15s;
}
.lbe-back-btn:hover { background: rgba(255,255,255,.18); }
.lbe-header-title { flex: 1; min-width: 0; }
.lbe-header-page { display: block; font-weight: 600; font-size: .9rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.lbe-header-sub { font-size: .72rem; opacity: .6; }
.lbe-header-tools { display: flex; align-items: center; gap: 10px; }
.lbe-device-toggle { display: flex; gap: 2px; background: rgba(255,255,255,.1); border-radius: 6px; padding: 3px; }
.lbe-dev-btn {
  background: none; border: none; color: rgba(255,255,255,.7); cursor: pointer;
  padding: 5px 8px; border-radius: 4px; display: flex; align-items: center;
}
.lbe-dev-btn.active { background: #4f46e5; color: #fff; }
.lbe-save-btn {
  background: #4f46e5; border: none; color: #fff; padding: 8px 20px;
  border-radius: 6px; font-size: .85rem; font-weight: 600; cursor: pointer;
  transition: background .15s;
}
.lbe-save-btn:hover:not(:disabled) { background: #4338ca; }
.lbe-save-btn:disabled { opacity: .5; cursor: not-allowed; }

/* Body */
.lbe-body {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* Left sidebar */
.lbe-sidebar {
  width: 240px;
  flex-shrink: 0;
  background: #fff;
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.lbe-sidebar-title {
  padding: 10px 14px 8px;
  font-size: .75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .05em;
  color: #94a3b8;
  flex-shrink: 0;
}
.lbe-sidebar-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 8px;
}
.lbe-empty-list {
  text-align: center;
  padding: 24px 8px;
  font-size: .8rem;
  color: #cbd5e1;
}
.lbe-block-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 7px;
  cursor: pointer;
  margin-bottom: 3px;
  border: 1.5px solid transparent;
  transition: background .12s, border-color .12s;
}
.lbe-block-item:hover { background: #f1f5f9; }
.lbe-block-item.selected { background: #eef2ff; border-color: #c7d2fe; }
.lbe-block-icon { font-size: 1rem; flex-shrink: 0; }
.lbe-block-name { flex: 1; font-size: .82rem; font-weight: 500; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #334155; }
.lbe-block-actions { display: flex; gap: 2px; opacity: 0; transition: opacity .15s; }
.lbe-block-item:hover .lbe-block-actions { opacity: 1; }
.lbe-block-item.selected .lbe-block-actions { opacity: 1; }
.lbe-act-btn {
  width: 22px; height: 22px;
  background: none; border: 1px solid #e2e8f0; border-radius: 4px;
  color: #64748b; cursor: pointer; font-size: .7rem;
  display: flex; align-items: center; justify-content: center;
  transition: background .1s, color .1s;
}
.lbe-act-btn:hover:not(:disabled) { background: #f1f5f9; }
.lbe-act-btn:disabled { opacity: .3; cursor: not-allowed; }
.lbe-act-btn.danger:hover:not(:disabled) { background: #fee2e2; color: #dc2626; border-color: #fca5a5; }

/* Add block section — fixed at bottom of sidebar */
.lbe-add-section {
  flex-shrink: 0;
  border-top: 1px solid #e2e8f0;
  padding: 10px 8px 14px;
  overflow-y: auto;
  max-height: 55%;
}
.lbe-add-title {
  font-size: .72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .05em;
  color: #94a3b8;
  margin-bottom: 8px;
  padding: 0 6px;
}
.lbe-add-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
}
.lbe-add-btn {
  display: flex; align-items: center; gap: 5px;
  padding: 7px 8px;
  background: none; border: 1px solid #e2e8f0;
  border-radius: 6px; cursor: pointer; font-size: .78rem;
  color: #475569; transition: background .12s, border-color .12s;
  white-space: nowrap; overflow: hidden;
}
.lbe-add-btn:hover { background: #f8fafc; border-color: #c7d2fe; color: #4f46e5; }

/* Props panel */
.lbe-props {
  width: 320px;
  flex-shrink: 0;
  background: #fff;
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.lbe-props-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #cbd5e1;
  font-size: .85rem;
  text-align: center;
}
.lbe-props-title {
  padding: 12px 16px 8px;
  font-size: .8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .05em;
  color: #94a3b8;
  flex-shrink: 0;
  border-bottom: 1px solid #f1f5f9;
}
.lbe-props > template + * { flex: 1; overflow-y: auto; padding: 12px 14px; }
.lbe-props template ~ .lbe-field,
.lbe-props template ~ .lbe-field-row,
.lbe-props template ~ .lbe-sub-title,
.lbe-props template ~ .lbe-sub-item,
.lbe-props template ~ .lbe-add-item-btn { margin-left: 14px; margin-right: 14px; }

/* When template renders inside lbe-props, show scrollable content */
.lbe-props > :not(.lbe-props-title):not(.lbe-props-empty) {
  overflow-y: auto;
  padding: 12px 14px;
  flex: 1;
}

/* Form fields */
.lbe-field { margin-bottom: 12px; }
.lbe-field label {
  display: block;
  font-size: .75rem;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 4px;
}
.lbe-field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 0; }
.lbe-field-row .lbe-field { margin-bottom: 12px; }
.lbe-field-check { display: flex; align-items: center; gap: 8px; }
.lbe-field-check label { margin: 0; }
.lbe-input, .lbe-select, .lbe-textarea {
  width: 100%;
  padding: 7px 10px;
  border: 1.5px solid #e2e8f0;
  border-radius: 6px;
  font-size: .82rem;
  font-family: inherit;
  color: #1e293b;
  background: #fff;
  outline: none;
  transition: border-color .15s;
}
.lbe-input:focus, .lbe-select:focus, .lbe-textarea:focus { border-color: #818cf8; }
.lbe-textarea { min-height: 80px; resize: vertical; font-family: 'Courier New', monospace; font-size: .78rem; }
.lbe-textarea-lg { min-height: 120px; }
.lbe-textarea-xl { min-height: 200px; }
.lbe-color { width: 100%; height: 36px; padding: 2px 4px; border: 1.5px solid #e2e8f0; border-radius: 6px; cursor: pointer; }
.lbe-sub-title {
  font-size: .75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .05em;
  color: #94a3b8;
  margin-bottom: 8px;
  margin-top: 4px;
}
.lbe-sub-item {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 8px;
}
.lbe-sub-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: .8rem;
  font-weight: 600;
  color: #475569;
  margin-bottom: 8px;
}
.lbe-add-item-btn {
  width: 100%;
  padding: 8px;
  background: none;
  border: 1.5px dashed #c7d2fe;
  border-radius: 6px;
  color: #6366f1;
  font-size: .8rem;
  cursor: pointer;
  margin-top: 4px;
  transition: background .12s;
  margin-bottom: 12px;
}
.lbe-add-item-btn:hover { background: #eef2ff; }

/* Preview panel */
.lbe-preview-panel {
  flex: 1;
  background: #e2e8f0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 20px;
  overflow: auto;
}
.lbe-preview-wrap {
  background: #fff;
  box-shadow: 0 4px 24px rgba(0,0,0,.12);
  border-radius: 8px;
  overflow: hidden;
  transition: width .3s, height .3s;
}
.lbe-preview-wrap.device-desktop {
  width: 100%;
  min-height: calc(100vh - 92px);
}
.lbe-preview-wrap.device-tablet {
  width: 768px;
  max-width: 100%;
  min-height: 900px;
  border-radius: 16px;
  border: 8px solid #334155;
}
.lbe-preview-wrap.device-mobile {
  width: 375px;
  min-height: 700px;
  border-radius: 20px;
  border: 6px solid #334155;
}
.lbe-iframe {
  display: block;
  width: 100%;
  min-height: calc(100vh - 92px);
  border: none;
}
.device-mobile .lbe-iframe {
  min-height: 700px;
}
.device-tablet .lbe-iframe {
  min-height: 900px;
}

/* Settings button */
.lbe-settings-btn {
  display: flex; align-items: center; gap: 5px;
  background: rgba(255,255,255,.1); border: none; color: rgba(255,255,255,.85);
  padding: 7px 12px; border-radius: 6px; font-size: .82rem; cursor: pointer;
  transition: background .15s;
}
.lbe-settings-btn:hover { background: rgba(255,255,255,.18); }

/* Settings overlay */
.lbe-settings-overlay {
  position: fixed; inset: 0; z-index: 300;
  background: rgba(0,0,0,.4);
  display: flex; align-items: center; justify-content: center;
}
.lbe-settings-panel {
  background: #fff;
  border-radius: 14px;
  width: 520px; max-width: 95vw; max-height: 88vh;
  display: flex; flex-direction: column;
  box-shadow: 0 8px 40px rgba(0,0,0,.2);
  overflow: hidden;
}
.lbe-settings-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 20px; border-bottom: 1px solid #e2e8f0;
  font-size: .95rem; font-weight: 700; color: #1e293b;
  flex-shrink: 0;
}
.lbe-settings-close {
  background: none; border: none; font-size: 1.1rem; cursor: pointer; color: #94a3b8; line-height: 1;
}
.lbe-settings-body { flex: 1; overflow-y: auto; padding: 20px; display: flex; flex-direction: column; gap: 18px; }
.lbe-settings-footer {
  display: flex; justify-content: flex-end; gap: 10px;
  padding: 14px 20px; border-top: 1px solid #e2e8f0; flex-shrink: 0;
}

/* Settings fields */
.lbe-sf { display: flex; flex-direction: column; gap: 6px; }
.lbe-sf-label { font-size: .8rem; font-weight: 700; color: #475569; }
.lbe-sf-hint { font-size: .72rem; color: #94a3b8; }
.lbe-sf-input, .lbe-sf-textarea {
  padding: 8px 12px; border: 1.5px solid #e2e8f0; border-radius: 8px;
  font-size: .85rem; font-family: inherit; color: #1e293b; background: #fff; outline: none;
  transition: border-color .15s;
}
.lbe-sf-input:focus, .lbe-sf-textarea:focus { border-color: #818cf8; }
.lbe-sf-textarea { resize: vertical; }
.lbe-sf-divider { height: 1px; background: #f1f5f9; margin: 2px 0; }
.lbe-sf-img-preview { margin-top: 8px; }
.lbe-sf-img-preview img { max-height: 100px; border-radius: 6px; border: 1px solid #e2e8f0; }

/* 全站主題 Modal (P3-1) */
.lbe-theme-presets { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 6px; }
.lbe-theme-preset { display: flex; align-items: center; gap: 5px; padding: 8px 10px; border: 1.5px solid #e2e8f0; border-radius: 8px; background: #fff; cursor: pointer; transition: border-color .12s, background .12s; }
.lbe-theme-preset:hover { border-color: #c7d2fe; background: #f8fafc; }
.lbe-theme-preset.active { border-color: #6366f1; background: #eef2ff; }
.lbe-theme-swatch { width: 16px; height: 16px; border-radius: 50%; flex-shrink: 0; box-shadow: inset 0 0 0 1px rgba(0,0,0,.08); }
.lbe-theme-preset-name { font-size: .78rem; color: #475569; margin-left: 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.lbe-theme-colors { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px; margin-top: 6px; }
.lbe-theme-color { display: flex; flex-direction: column; gap: 4px; }
.lbe-theme-color span { font-size: .72rem; color: #64748b; }
.lbe-theme-segment { display: flex; gap: 4px; flex-wrap: wrap; margin-top: 6px; }
.lbe-seg-btn { padding: 6px 12px; border: 1.5px solid #e2e8f0; border-radius: 6px; background: #fff; cursor: pointer; font-size: .78rem; color: #475569; transition: border-color .12s, background .12s, color .12s; }
.lbe-seg-btn:hover { border-color: #c7d2fe; }
.lbe-seg-btn.active { border-color: #6366f1; background: #6366f1; color: #fff; }

/* 進階樣式：觸發按鈕 + 彈出視窗 (P3-2) */
.lbe-adv-open { display: flex; align-items: center; justify-content: center; gap: 8px; width: calc(100% - 28px); margin: 12px 14px 4px; padding: 10px; background: #eef2ff; border: 1.5px solid #c7d2fe; border-radius: 8px; cursor: pointer; font-size: .85rem; font-weight: 700; color: #4f46e5; transition: background .12s, border-color .12s; }
.lbe-adv-open:hover { background: #e0e7ff; border-color: #a5b4fc; }
.lbe-adv-dot { color: #6366f1; font-size: .7rem; }
.lbe-adv-modal { width: 760px; max-width: 96vw; }
.lbe-adv-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
.lbe-adv-col { display: flex; flex-direction: column; gap: 10px; }
.lbe-adv-coltitle { font-size: .8rem; font-weight: 700; color: #475569; padding-bottom: 6px; border-bottom: 1px solid #f1f5f9; }
@media (max-width: 640px) { .lbe-adv-grid { grid-template-columns: 1fr; } }
.lbe-contrast-warn { margin: 10px 14px 0; padding: 8px 10px; background: #fffbeb; border: 1px solid #fde68a; border-radius: 6px; font-size: .74rem; color: #92400e; line-height: 1.5; }
.lbe-adv-sub { margin: 12px 14px 4px; font-size: .74rem; font-weight: 700; color: #64748b; }
.lbe-adv-check { display: flex; align-items: center; gap: 8px; margin: 4px 14px; font-size: .82rem; color: #334155; cursor: pointer; }
.lbe-adv-check input { width: 15px; height: 15px; cursor: pointer; }
.lbe-field-adv { margin: 8px 0 2px; }
.lbe-field-adv > summary { font-size: .76rem; color: #6366f1; cursor: pointer; padding: 4px 0; user-select: none; }
.lbe-field-adv[open] > summary { margin-bottom: 6px; }
.lbe-style-clip { display: flex; gap: 6px; margin: 12px 14px 0; }
.lbe-clip-btn { flex: 1; padding: 7px 8px; background: #fff; border: 1px solid #e2e8f0; border-radius: 6px; font-size: .76rem; color: #475569; cursor: pointer; transition: background .12s, border-color .12s; }
.lbe-clip-btn:hover:not(:disabled) { background: #f8fafc; border-color: #c7d2fe; color: #4f46e5; }
.lbe-clip-btn:disabled { opacity: .45; cursor: not-allowed; }

/* Image Picker */
.lbe-img-picker { border: 1.5px dashed #cbd5e1; border-radius: 8px; padding: 10px; background: #f8fafc; outline: none; transition: border-color .2s; }
.lbe-img-picker:focus, .lbe-img-picker:focus-within { border-color: #6366f1; background: #f5f3ff; }
.lbe-img-picker-sm { padding: 8px; }
.lbe-img-picker-preview { position: relative; display: inline-block; margin-bottom: 8px; }
.lbe-img-picker-preview img { max-height: 120px; max-width: 100%; border-radius: 6px; border: 1px solid #e2e8f0; display: block; }
.lbe-img-picker-sm .lbe-img-picker-preview img { max-height: 80px; }
.lbe-img-picker-clear { position: absolute; top: -6px; right: -6px; width: 20px; height: 20px; border-radius: 50%; background: #ef4444; color: #fff; border: none; cursor: pointer; font-size: 10px; line-height: 20px; text-align: center; padding: 0; }
.lbe-img-picker-placeholder { font-size: .78rem; color: #94a3b8; text-align: center; padding: 8px 0; margin-bottom: 8px; }
.lbe-img-picker-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.lbe-img-btn { display: inline-flex; align-items: center; gap: 4px; padding: 5px 10px; border-radius: 6px; background: #6366f1; color: #fff; font-size: .78rem; font-weight: 600; cursor: pointer; white-space: nowrap; flex-shrink: 0; }
.lbe-img-btn:hover { background: #4f46e5; }
.lbe-img-or { font-size: .75rem; color: #94a3b8; flex-shrink: 0; }
.lbe-img-url-input { flex: 1; min-width: 0; font-size: .78rem !important; padding: 5px 8px !important; }
.lbe-img-hint { font-size: .75rem; color: #6366f1; margin-top: 4px; }
.lbe-img-error { font-size: .75rem; color: #ef4444; margin-top: 4px; }

/* Status toggle */
.lbe-status-toggle { display: flex; gap: 8px; }
.lbe-status-btn {
  flex: 1; padding: 10px; border: 1.5px solid #e2e8f0; border-radius: 8px;
  background: #f8fafc; font-size: .85rem; cursor: pointer; font-weight: 500; color: #64748b;
  transition: all .15s;
}
.lbe-status-btn.active-pub { border-color: #22c55e; background: #f0fdf4; color: #16a34a; }
.lbe-status-btn.active-draft { border-color: #f59e0b; background: #fffbeb; color: #b45309; }

/* Undo / Redo */
.lbe-undo-group { display: flex; gap: 2px; background: rgba(255,255,255,.1); border-radius: 6px; padding: 2px; }
.lbe-undo-btn {
  background: none; border: none; color: rgba(255,255,255,.8); cursor: pointer;
  padding: 5px 9px; border-radius: 4px; font-size: .95rem; line-height: 1;
  transition: background .12s;
}
.lbe-undo-btn:hover:not(:disabled) { background: rgba(255,255,255,.15); }
.lbe-undo-btn:disabled { opacity: .3; cursor: not-allowed; }

/* Auto-save / unsaved */
.lbe-autosave-status { font-size: .72rem; white-space: nowrap; padding: 0 4px; }
.lbe-autosave-status.as-saving { color: rgba(255,255,255,.6); }
.lbe-autosave-status.as-saved  { color: #4ade80; }
.lbe-autosave-status.as-error  { color: #f87171; }
.lbe-unsaved-dot { color: #f59e0b; font-size: .7rem; line-height: 1; padding: 0 2px; }

/* Drag handle */
.lbe-drag-handle {
  cursor: grab; color: #94a3b8; font-size: 1rem; flex-shrink: 0;
  padding: 0 2px; transition: color .1s; user-select: none;
}
.lbe-drag-handle:active { cursor: grabbing; }
.lbe-block-item:hover .lbe-drag-handle { color: #64748b; }

/* Version history modal items */
.lbe-version-item {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 12px 0; border-bottom: 1px solid #f1f5f9;
}
.lbe-version-item:last-child { border-bottom: none; }
.lbe-version-info { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.lbe-version-num { font-size: .85rem; font-weight: 700; color: #1e293b; }
.lbe-version-time { font-size: .78rem; color: #64748b; }
.lbe-version-meta { font-size: .72rem; color: #94a3b8; }

/* Buttons in settings footer */
.lbe-btn-cancel {
  padding: 8px 18px; border: 1.5px solid #e2e8f0; border-radius: 7px;
  background: #fff; color: #64748b; font-size: .85rem; cursor: pointer;
  transition: background .15s;
}
.lbe-btn-cancel:hover { background: #f8fafc; }
.lbe-btn-save {
  padding: 8px 20px; border: none; border-radius: 7px;
  background: #4f46e5; color: #fff; font-size: .85rem; font-weight: 600; cursor: pointer;
  transition: background .15s;
}
.lbe-btn-save:hover:not(:disabled) { background: #4338ca; }
.lbe-btn-save:disabled { opacity: .5; cursor: not-allowed; }
</style>
